import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
// html2canvas is loaded on demand in the screenshot handler (kept out of the initial bundle).
import { useNavigate } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import InfoTip from "../components/shared/InfoTip";
import { SkeletonChart, SkeletonMetricCard } from "../components/shared/Skeleton";
import ClientHeader from "../components/ClientHeader";
import SessionDetailPanel from "../components/SessionDetailPanel";
import CartEmailModal from "../components/CartEmailModal";
import NavSidebar from "../components/NavSidebar";
import { usePageMeta } from "../components/DashboardShell";
import { useFeatures } from "../contexts/FeaturesContext";
import { useTheme } from "../contexts/ThemeContext";
import { useView } from "../contexts/ViewContext";
import googleAdsIconDark from "../assets/icons/google-ads-dark.svg";
import googleAdsIconLight from "../assets/icons/google-ads-light.svg";
import { getBookingKpis, getBookingFunnel, getBookingSessions, getBookingAbandonedCarts, getBookingInsights, getBookingTrends, getMyPreferences, updateMyPreferences, getCartEmailStatus, getBookingDemandCalendar, getBookingWindow, getBookingPriceSensitivity, getBookingCrossBrowse, getBookingDayOfWeek, getBookingRvIntelligence, getBookingPetDemand, getRecoveryConfig, updateRecoveryConfig } from "../api";
import { ComposedChart, Line, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import DateRangePicker from "../components/DateRangePicker";
import DemandCalendarPanel from "../components/booking/DemandCalendarPanel";
import BookingWindowPanel from "../components/booking/BookingWindowPanel";
import PriceSensitivityPanel from "../components/booking/PriceSensitivityPanel";
import CrossBrowsePanel from "../components/booking/CrossBrowsePanel";
import DayOfWeekPanel from "../components/booking/DayOfWeekPanel";
import RvIntelligencePanel from "../components/booking/RvIntelligencePanel";
import PetDemandPanel from "../components/booking/PetDemandPanel";
import RecoveryPanel from "../components/RecoveryPanel";
import { displayLabel } from "../utils/format";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const STATE_ABBR = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
  Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH",
  Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT",
  Virginia: "VA", Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
  "District of Columbia": "DC",
};

/* ----------------------------- helpers ----------------------------- */
const fmtDate = (d) => {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt?.getTime?.())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch { return ""; }
};
const fmtDisplayDate = (isoStr) => {
  if (!isoStr) return "";
  const [y, m, d] = isoStr.split("-");
  return `${m}/${d}/${y}`;
};
const startOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const fmtDollar = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct    = (v) => Number(v || 0).toFixed(1) + "%";
const fmtNum    = (v) => Number(v || 0).toLocaleString();

function relativeTime(dateStr) {
  if (!dateStr) return "—";
  const now = new Date();
  const dt = new Date(dateStr);
  const diffMs = now - dt;
  if (diffMs < 0) return dt.toLocaleDateString();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(diffMs / 3600000);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(diffMs / 86400000);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return dt.toLocaleDateString();
}

function fullTimestamp(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", second: "2-digit",
    hour12: true, timeZoneName: "short",
  });
}

const OUTCOME_CONFIG = {
  booked:         { label: "Booked",    bg: "bg-emerald-100 dark:bg-emerald-900/40", text: "text-emerald-700 dark:text-emerald-400" },
  abandoned_cart: { label: "Abandoned", bg: "bg-amber-100 dark:bg-amber-900/40",    text: "text-amber-700 dark:text-amber-400" },
  lead_captured:  { label: "Lead",      bg: "bg-blue-100 dark:bg-blue-900/40",      text: "text-blue-700 dark:text-blue-400" },
  browsing:       { label: "Browsing",  bg: "bg-gray-100 dark:bg-gray-800",          text: "text-gray-600 dark:text-gray-400" },
};

const STEP_LABELS = {
  search: "Search", results: "Results", details: "Details", cart: "Cart",
  checkout: "Payment", checkout_contact: "Payment", checkout_payment: "Payment", payment: "Payment", confirmation: "Booked",
};

const DEVICE_ICONS = {
  Desktop: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
    </svg>
  ),
  Mobile: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" />
    </svg>
  ),
  Tablet: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="4" y="2" width="16" height="20" rx="2" /><path d="M12 18h.01" />
    </svg>
  ),
  Unknown: (
    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
    </svg>
  ),
};

function computeDelta(current, previous) {
  if (!previous || previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

/* ----------------------------- column config ----------------------------- */
const SESSION_COLUMNS = [
  { key: "first_seen_at",        label: "Time",          width: "w-28", sortKey: "first_seen_at",        locked: true  },
  { key: "visitor_name",         label: "Visitor",        width: "w-28", sortKey: "visitor_name",         locked: true  },
  { key: "contact",              label: "Contact",        width: "w-40", sortKey: null,                   locked: false },
  { key: "check_in",             label: "Check-in",       width: "w-28", sortKey: null,                   locked: false },
  { key: "guests",               label: "Guests",         width: "w-16", sortKey: null,                   locked: false },
  { key: "nights",               label: "Nights",         width: "w-16", sortKey: "num_nights_mentioned", locked: false },
  { key: "site_category",        label: "Site Type",      width: "w-24", sortKey: "site_category",        locked: false },
  { key: "specific_site_type",   label: "Site Name",      width: "w-28", sortKey: null,                   locked: false },
  { key: "deepest_step",         label: "Step",           width: "w-20", sortKey: "deepest_step",         locked: false },
  { key: "outcome",              label: "Outcome",        width: "w-24", sortKey: null,                   locked: false },
  { key: "site_total",           label: "Value",          width: "w-20", sortKey: "site_total",           locked: false },
  { key: "nightly_rate",         label: "Rate",           width: "w-20", sortKey: null,                   locked: false },
  { key: "location",             label: "Location",       width: "w-28", sortKey: null,                   locked: false },
  { key: "pets",                 label: "Pets",           width: "w-14", sortKey: null,                   locked: false },
  { key: "rv_type",              label: "RV Type",        width: "w-24", sortKey: null,                   locked: false },
  { key: "referral_source",      label: "Referral",       width: "w-24", sortKey: null,                   locked: false },
  { key: "booking_window",       label: "Lead Days",      width: "w-20", sortKey: null,                   locked: false },
  { key: "rv_length",            label: "RV Length",      width: "w-20", sortKey: null,                   locked: false },
  { key: "equipment",            label: "Equipment",      width: "w-28", sortKey: null,                   locked: false },
  { key: "site_number",          label: "Site #",         width: "w-16", sortKey: null,                   locked: false },
  { key: "last_activity",        label: "Last Active",    width: "w-28", sortKey: "last_activity_at",     locked: false },
  { key: "email",                label: "Email",          width: "w-36", sortKey: "visitor_email",        locked: false },
  { key: "phone",                label: "Phone",          width: "w-28", sortKey: null,                   locked: false },
  { key: "device",               label: "Device",         width: "w-20", sortKey: null,                   locked: false },
  { key: "check_out",            label: "Check-out",      width: "w-24", sortKey: null,                   locked: false },
  { key: "transaction_id",       label: "Transaction",    width: "w-28", sortKey: null,                   locked: false },
];
const DEFAULT_VISIBLE_COLS = ["first_seen_at", "visitor_name", "contact", "check_in", "guests", "site_category", "deepest_step", "outcome", "site_total"];

/* ----------------------------- components ----------------------------- */

const DeltaBadge = ({ current, previous }) => {
  // Hide if either period has no meaningful data
  if (!current && !previous) return null;
  if ((!current || current === 0) && (!previous || previous === 0)) return null;
  if (!previous || previous === 0) return null;
  if (!current || current === 0) return null;

  const delta = computeDelta(current, previous);
  const isPositive = delta > 0;
  const isNegative = delta < 0;

  const colorClasses = isPositive
    ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
    : isNegative
    ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400"
    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400";

  const Arrow = isPositive ? (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
    </svg>
  ) : isNegative ? (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  ) : null;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-semibold ${colorClasses}`}>
      {Arrow}
      {delta >= 0 ? "+" : ""}{delta}%
    </span>
  );
};

const KpiCard = ({ title, value, subtitle, delta, variant = "primary", icon, loading = false, onClick, tooltip, sparkData }) => {
  const accentBorders = {
    primary: "border-t-[#32B1FF]",
    success: "border-t-teal-400 dark:border-t-teal-500",
    muted:   "border-t-sky-400 dark:border-t-sky-500",
    subtle:  "border-t-indigo-400 dark:border-t-indigo-500",
    warning: "border-t-amber-400 dark:border-t-amber-500",
    rose:    "border-t-rose-400 dark:border-t-rose-500",
  };

  const iconColors = {
    primary: "text-[#32B1FF]/10 dark:text-[#32B1FF]/5",
    success: "text-teal-400/10 dark:text-teal-500/5",
    muted:   "text-sky-400/10 dark:text-sky-500/5",
    subtle:  "text-indigo-400/10 dark:text-indigo-500/5",
    warning: "text-amber-400/10 dark:text-amber-500/5",
    rose:    "text-rose-400/10 dark:text-rose-500/5",
  };

  return (
    <div
      onClick={onClick}
      className={`relative bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 border-t-[3px] ${accentBorders[variant] ?? accentBorders.primary} p-6 pb-4 min-h-[140px] flex flex-col transition-shadow ${onClick ? "cursor-pointer hover:shadow-md" : ""}`}
    >
      <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
        <div className={`absolute -top-6 -right-6 ${iconColors[variant] ?? iconColors.primary}`}>
          <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
            {icon}
          </svg>
        </div>
      </div>
      <div className="flex items-center gap-1.5 relative z-20">
        <div className="text-sm uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">{title}</div>
        {tooltip && <InfoTip text={tooltip} />}
        {delta}
      </div>
      {loading ? (
        <div className="mt-2 flex items-center gap-2 relative z-10">
          <svg className="animate-spin h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-lg text-gray-400 dark:text-gray-500">Loading...</span>
        </div>
      ) : (
        <div className="mt-2 text-3xl font-extrabold text-gray-900 dark:text-gray-100 relative z-10">{value}</div>
      )}
      {subtitle && <div className="text-sm mt-1.5 text-gray-500 dark:text-gray-400 relative z-10">{subtitle}</div>}
      {sparkData?.length > 1 && !loading && (
        <div className="mt-auto pt-1 pb-3 -mx-6 relative z-10 overflow-hidden">
          <ResponsiveContainer width="100%" height={52}>
            <AreaChart data={sparkData} margin={{ top: 8, right: 0, left: 0, bottom: 6 }}>
              <defs>
                <linearGradient id="sparkUniform" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.18} />
                  <stop offset="100%" stopColor="#94a3b8" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <YAxis
                hide
                domain={[
                  (dataMin) => dataMin - (Math.abs(dataMin) * 0.05 || 1),
                  (dataMax) => dataMax + (Math.abs(dataMax) * 0.15 || 1),
                ]}
              />
              <Area type="natural" dataKey="v" stroke="#94a3b8" strokeWidth={1.2} fill="url(#sparkUniform)" dot={false} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ============================= ICONS ============================= */
const IconSessions = <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>;
const IconBooking  = <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M9 16l2 2 4-4" /></>;
const IconConvert  = <><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></>;
const IconCart     = <><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></>;
const IconLead     = <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></>;
const IconAvg      = <><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>;

/* ============================= PAGE ============================= */
const BookingsPage = () => {
  usePageMeta({ headerActive: 'Customers', sidebarActive: 'booking' });
  const username = localStorage.getItem("username") || "Admin";
  const navigate = useNavigate();
  const { hasFeature } = useFeatures();
  const { isDarkMode } = useTheme();
  const view = useView();
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const isClient = view.isClient;
  const dashboardPath = isClient ? "/client-dashboard" : "/dashboard";
  // 2026-07-31 — Recovery is always available when Bookings is active. This
  // page is FeatureRoute-gated on `bookings` in App.js, so simply being here
  // means Bookings is on for the current property. Was: gated on the separate
  // `cart_email` (legacy) / `dashboard.recovery` (Supabase) flag, which meant
  // properties with Bookings but no explicit recovery grant hid the button.
  const canSendCartEmail = true;
  // 2026-08-13 — Session details (guest journey panel) is always available when
  // Bookings is active. This page is FeatureRoute-gated on `bookings` in App.js,
  // so simply being here means Bookings is on for the current property. Was:
  // gated on `hasFeature('session_details')` → `feature.session_details`, but
  // that flag was never registered in the feature-flags system (not in
  // featureCatalog.js, not in ClientFeatureToggles.js, no feature_flags row),
  // so the visitor name was never clickable — the guest journey panel never
  // opened. Same fix pattern as canSendCartEmail above (2026-07-31).
  const canViewSessionDetails = true;
  const canCustomColumns = hasFeature('custom_columns');

  /* ---- responsive: rotate trend chart XAxis labels (-30° <500, -25° 500–799, 0° ≥800) ---- */
  const computeChartLabelAngle = () => {
    if (typeof window === "undefined") return 0;
    if (window.innerWidth < 500) return -30;
    if (window.innerWidth < 800) return -25;
    return 0;
  };
  const [chartLabelAngle, setChartLabelAngle] = useState(computeChartLabelAngle);
  useEffect(() => {
    const check = () => setChartLabelAngle(computeChartLabelAngle());
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* ---- cart email state ---- */
  const [emailStatuses, setEmailStatuses] = useState({});
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalCart, setEmailModalCart] = useState(null);
  const [autoEmailEnabled, setAutoEmailEnabled] = useState(false);
  const [savingAutoEmail, setSavingAutoEmail] = useState(false);
  const [recoveryConfig, setRecoveryConfig] = useState(null);
  const [autoSendError, setAutoSendError] = useState("");

  /* ---- clients ---- */
  const INSIDER_PERKS_KEY = "insider-perks-rv";
  const VERDE_ID = "cd94da9a-42af-4c73-8b6a-467469e23cbe";
  const ALL_CLIENTS_MAP = {
    [INSIDER_PERKS_KEY]: "Insider Perks RV",
    [VERDE_ID]: "Verde Ranch RV Resort",
    "92a6a5b3-c532-42e6-889c-08a0feb783d0": "American RV Resort",
    "aa58cf1c-e977-4d41-8e8b-9f3326338643": "Savannah Lakes RV Resort",
    "fc9d80fd-e461-4880-a393-647f49d7abf6": "Rock Springs RV Resort",
    "37d6d70b-4b7a-47b2-b51c-5ce2d2d78577": "Lagoon Ranch RV Resort",
    "8c344570-5cab-4930-b212-4fc27f1ad935": "COTA RV Park",
    "55b173a6-fb11-409b-811b-b83deee00a8c": "Coachella Lakes RV Resort",
    "d797becc-e267-4877-a1a3-862f37797435": "Buckeye Ranch RV Resort",
    "219035d2-1212-4be0-af79-13db9310c1b0": "Rio Guadalupe RV Resort",
    "72d8ced4-3275-4713-b446-53948d671871": "Jellystone Park Hill Country",
  };
  const ALL_CLIENTS_LIST = (() => {
    const sorted = Object.entries(ALL_CLIENTS_MAP)
      .filter(([id]) => id !== INSIDER_PERKS_KEY)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: INSIDER_PERKS_KEY, name: ALL_CLIENTS_MAP[INSIDER_PERKS_KEY] }, ...sorted];
  })();
  const DEFAULT_CLIENT_ID = INSIDER_PERKS_KEY;
  // Resolve the actual API client ID — use ViewContext supabase ID if project selected, otherwise local logic
  const resolveClientId = (key) => {
    if (view.supabaseClientId) return view.supabaseClientId;
    return key === INSIDER_PERKS_KEY ? VERDE_ID : key;
  };
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  // Was `Number(localStorage.getItem("role") || 0) === 0`; the localStorage read
  // was removed in b66b1fd but this line kept referencing the now-undefined
  // `userRole`, crashing BookingsPage on mount. ViewContext exposes the
  // org-resolved `isSuperAdmin` (platform admin) — same semantics, one source.
  //
  // Epic 4 (item 35 Q2) — the six full-access staff get the full client list
  // too, via the same canOpenClientDashboard flag (true for exactly platform
  // admin + the six). Design rule: isPlatformAdmin || canOpenClientDashboard
  // at every in-client-dashboard gate; never make isPlatformAdmin itself true
  // for the six (that would leak admin screens — see Epic 4b). Admin screens
  // (UserManagement, AddEditUserModal) keep gating on isPlatformAdmin alone.
  const isSuperAdmin = view.isSuperAdmin || view.canOpenClientDashboard;
  const [allowedClients, setAllowedClients] = useState(isSuperAdmin ? ALL_CLIENTS_LIST : []);
  const [selectedClientId, setSelectedClientId] = useState(isSuperAdmin ? DEFAULT_CLIENT_ID : "");
  const [clientsLoading, setClientsLoading] = useState(!isSuperAdmin);
  const isInsiderPerks = selectedClientId === INSIDER_PERKS_KEY;
  // What we actually hit the API with. Falls through to the global project's
  // supabase_client_id when set, so client users with no local picker still fetch.
  const effectiveClientId = resolveClientId(selectedClientId);

  /* ---- date range — single source of truth from ViewContext (header global filter) ---- */
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const rangeStart = view.rangeStart;
  const rangeEnd = view.rangeEnd;
  const setRangeStart = (v) => view.setDateRange(v, view.rangeEnd);
  const setRangeEnd = (v) => view.setDateRange(view.rangeStart, v);

  /* ---- KPIs ---- */
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisError, setKpisError] = useState("");

  /* ---- Funnel ---- */
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);

  /* ---- Refs ---- */
  const sessionTableRef = useRef(null);

  /* ---- Sessions table ---- */
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPag, setSessionsPag] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });
  const [sessionsSort, setSessionsSort] = useState({ col: "first_seen_at", dir: "desc" });
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [hasContactFilter, setHasContactFilter] = useState("");
  const [siteCatFilter, setSiteCatFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [checkinDateFilter, setCheckinDateFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [siteCategories, setSiteCategories] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionPanelOpen, setSessionPanelOpen] = useState(false);
  const [recoveryPanelOpen, setRecoveryPanelOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("session_columns_view");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) {
          const validKeys = new Set(SESSION_COLUMNS.map(c => c.key));
          const filtered = parsed.filter(k => validKeys.has(k));
          // Reset to defaults if saved prefs are from the old layout (had "intent" or missing new defaults)
          if (parsed.includes("intent") || !filtered.includes("check_in")) {
            localStorage.removeItem("session_columns_view");
            return DEFAULT_VISIBLE_COLS;
          }
          if (filtered.length) return filtered;
        }
      }
    } catch {}
    return DEFAULT_VISIBLE_COLS;
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [savedViewMsg, setSavedViewMsg] = useState("");
  const columnPickerRef = useRef(null);

  // Load saved column preferences from API
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const resp = await getMyPreferences();
        const prefs = resp?.data?.preferences || {};
        if (Array.isArray(prefs.session_columns) && prefs.session_columns.length) {
          // Skip stale prefs from old layout
          if (!prefs.session_columns.includes("intent") && prefs.session_columns.includes("check_in")) {
            setVisibleColumns(prefs.session_columns);
            localStorage.setItem("session_columns_view", JSON.stringify(prefs.session_columns));
          }
        }
        // auto_abandoned_email preference is now replaced by recovery config (clients.auto_recovery_enabled)
        if (Array.isArray(prefs.allowed_clients) && prefs.allowed_clients.length) {
          const mapped = prefs.allowed_clients
            .filter(id => ALL_CLIENTS_MAP[id])
            .map(id => ({ id, name: ALL_CLIENTS_MAP[id] }))
            .sort((a, b) => a.name.localeCompare(b.name));
          if (mapped.length) {
            setAllowedClients(mapped);
            if (!mapped.find(c => c.id === selectedClientId)) {
              setSelectedClientId(mapped[0].id);
            }
          }
        }
      } catch {}
      finally { setClientsLoading(false); }
    };
    loadPrefs();
  }, []);

  // Close column picker on outside click
  useEffect(() => {
    if (!showColumnPicker) return;
    const handler = (e) => { if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) setShowColumnPicker(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showColumnPicker]);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const handleSaveView = async () => {
    setSavingView(true);
    try {
      await updateMyPreferences({ session_columns: visibleColumns });
      localStorage.setItem("session_columns_view", JSON.stringify(visibleColumns));
      setSavedViewMsg("Saved!");
      setTimeout(() => setSavedViewMsg(""), 2000);
    } catch { setSavedViewMsg("Failed"); setTimeout(() => setSavedViewMsg(""), 2000); }
    finally { setSavingView(false); }
  };

  const isColVisible = (key) => visibleColumns.includes(key);

  /* ---- Abandoned carts ---- */
  const [abandonedCarts, setAbandonedCarts] = useState([]);
  const [abandonedLoading, setAbandonedLoading] = useState(false);
  const [abandonedCopiedId, setAbandonedCopiedId] = useState(null);
  const [abandonedCopiedAll, setAbandonedCopiedAll] = useState(false);
  const [abandonedPage, setAbandonedPage] = useState(1);
  const [abandonedSort, setAbandonedSort] = useState("newest"); // "newest" | "value"
  const ABANDONED_PER_PAGE = 10;

  /* ---- Insights ---- */
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  /* ---- New Panels ---- */
  const [demandCalendar, setDemandCalendar] = useState(null);
  const [demandCalendarLoading, setDemandCalendarLoading] = useState(false);
  const [demandCalendarLookahead, setDemandCalendarLookahead] = useState(90);
  const [bookingWindow, setBookingWindow] = useState(null);
  const [bookingWindowLoading, setBookingWindowLoading] = useState(false);
  const [priceSensitivity, setPriceSensitivity] = useState(null);
  const [priceSensitivityLoading, setPriceSensitivityLoading] = useState(false);
  const [crossBrowse, setCrossBrowse] = useState(null);
  const [crossBrowseLoading, setCrossBrowseLoading] = useState(false);
  const [dayOfWeek, setDayOfWeek] = useState(null);
  const [dayOfWeekLoading, setDayOfWeekLoading] = useState(false);
  const [rvIntelligence, setRvIntelligence] = useState(null);
  const [rvIntelligenceLoading, setRvIntelligenceLoading] = useState(false);
  const [petDemand, setPetDemand] = useState(null);
  const [petDemandLoading, setPetDemandLoading] = useState(false);

  // Load recovery config when client changes
  useEffect(() => {
    if (!effectiveClientId || !canSendCartEmail) return;
    const loadRecoveryConfig = async () => {
      try {
        const resp = await getRecoveryConfig(resolveClientId(selectedClientId));
        const cfg = resp?.data;
        setRecoveryConfig(cfg || null);
        setAutoEmailEnabled(!!cfg?.auto_recovery_enabled);
      } catch (e) {
        console.error("Failed to load recovery config:", e);
        setRecoveryConfig(null);
      }
    };
    loadRecoveryConfig();
  }, [selectedClientId, view.supabaseClientId]);

  const handleToggleAutoEmail = async () => {
    setSavingAutoEmail(true);
    setAutoSendError("");
    const newVal = !autoEmailEnabled;
    try {
      await updateRecoveryConfig(resolveClientId(selectedClientId), { auto_recovery_enabled: newVal });
      setAutoEmailEnabled(newVal);
      // Refresh config to get updated gate status
      const resp = await getRecoveryConfig(resolveClientId(selectedClientId));
      setRecoveryConfig(resp?.data || null);
    } catch (err) {
      const msg = err?.message || "Failed to update auto-send";
      setAutoSendError(msg);
      // Revert the toggle
      setAutoEmailEnabled(!newVal);
      setTimeout(() => setAutoSendError(""), 5000);
    } finally {
      setSavingAutoEmail(false);
    }
  };

  /* ---- Trends ---- */
  const [trends, setTrends] = useState([]);
  const [trendsLoading, setTrendsLoading] = useState(false);

  /* ---- clients: hardcoded for now ---- */

  /* ---- fetch KPIs + funnel when client or dates change ---- */
  useEffect(() => {
    if (!effectiveClientId || !rangeStart || !rangeEnd) return;

    const loadKpis = async () => {
      try {
        setKpisLoading(true);
        setKpisError("");
        const resp = await getBookingKpis(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setKpis(resp?.data ?? null);
      } catch (e) {
        setKpisError(e?.message || "Failed to fetch KPIs");
        setKpis(null);
      } finally {
        setKpisLoading(false);
      }
    };

    const loadFunnel = async () => {
      try {
        setFunnelLoading(true);
        const resp = await getBookingFunnel(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setFunnel(resp?.data ?? null);
      } catch (e) {
        console.error("Failed to fetch funnel:", e);
        setFunnel(null);
      } finally {
        setFunnelLoading(false);
      }
    };

    const loadAbandoned = async () => {
      try {
        setAbandonedLoading(true);
        setAbandonedPage(1);
        const resp = await getBookingAbandonedCarts(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        const carts = resp?.data?.carts ?? [];
        setAbandonedCarts(carts);
        // Fetch email statuses for loaded carts
        if (canSendCartEmail && carts.length) {
          try {
            const statusResp = await getCartEmailStatus(resolveClientId(selectedClientId), rangeStart, rangeEnd);
            setEmailStatuses(statusResp?.data?.statuses || {});
          } catch {}
        }
      } catch (e) {
        console.error("Failed to fetch abandoned carts:", e);
        setAbandonedCarts([]);
      } finally {
        setAbandonedLoading(false);
      }
    };

    const loadInsights = async () => {
      try {
        setInsightsLoading(true);
        const resp = await getBookingInsights(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setInsights(resp?.data ?? null);
      } catch (e) {
        console.error("Failed to fetch insights:", e);
        setInsights(null);
      } finally {
        setInsightsLoading(false);
      }
    };

    const loadTrends = async () => {
      try {
        setTrendsLoading(true);
        const resp = await getBookingTrends(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setTrends(resp?.data?.days ?? []);
      } catch (e) {
        console.error("Failed to fetch trends:", e);
        setTrends([]);
      } finally {
        setTrendsLoading(false);
      }
    };

    const loadDemandCalendar = async () => {
      try {
        setDemandCalendarLoading(true);
        const resp = await getBookingDemandCalendar(resolveClientId(selectedClientId), rangeStart, rangeEnd, demandCalendarLookahead);
        setDemandCalendar(resp?.data?.calendar ?? null);
      } catch (e) {
        console.error("Failed to fetch demand calendar:", e);
        setDemandCalendar(null);
      } finally { setDemandCalendarLoading(false); }
    };

    const loadBookingWindow = async () => {
      try {
        setBookingWindowLoading(true);
        const resp = await getBookingWindow(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setBookingWindow(resp?.data?.site_types ?? null);
      } catch (e) {
        console.error("Failed to fetch booking window:", e);
        setBookingWindow(null);
      } finally { setBookingWindowLoading(false); }
    };

    const loadPriceSensitivity = async () => {
      try {
        setPriceSensitivityLoading(true);
        const resp = await getBookingPriceSensitivity(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setPriceSensitivity(resp?.data?.buckets ?? null);
      } catch (e) {
        console.error("Failed to fetch price sensitivity:", e);
        setPriceSensitivity(null);
      } finally { setPriceSensitivityLoading(false); }
    };

    const loadCrossBrowse = async () => {
      try {
        setCrossBrowseLoading(true);
        const resp = await getBookingCrossBrowse(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setCrossBrowse(resp?.data ?? null);
      } catch (e) {
        console.error("Failed to fetch cross-browse:", e);
        setCrossBrowse(null);
      } finally { setCrossBrowseLoading(false); }
    };

    const loadDayOfWeek = async () => {
      try {
        setDayOfWeekLoading(true);
        const resp = await getBookingDayOfWeek(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setDayOfWeek(resp?.data ?? null);
      } catch (e) {
        console.error("Failed to fetch day-of-week:", e);
        setDayOfWeek(null);
      } finally { setDayOfWeekLoading(false); }
    };

    const loadRvIntelligence = async () => {
      try {
        setRvIntelligenceLoading(true);
        const resp = await getBookingRvIntelligence(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setRvIntelligence(resp?.data ?? null);
      } catch (e) {
        console.error("Failed to fetch RV intelligence:", e);
        setRvIntelligence(null);
      } finally { setRvIntelligenceLoading(false); }
    };

    const loadPetDemand = async () => {
      try {
        setPetDemandLoading(true);
        const resp = await getBookingPetDemand(resolveClientId(selectedClientId), rangeStart, rangeEnd);
        setPetDemand(resp?.data ?? null);
      } catch (e) {
        console.error("Failed to fetch pet demand:", e);
        setPetDemand(null);
      } finally { setPetDemandLoading(false); }
    };

    loadKpis();
    loadFunnel();
    loadAbandoned();
    loadInsights();
    loadTrends();
    loadDemandCalendar();
    loadBookingWindow();
    loadPriceSensitivity();
    loadCrossBrowse();
    loadDayOfWeek();
    loadRvIntelligence();
    loadPetDemand();
  }, [selectedClientId, rangeStart, rangeEnd, view.supabaseClientId]);

  /* ---- reload demand calendar when lookahead toggle changes ---- */
  const handleDemandCalendarLookahead = async (days) => {
    if (!effectiveClientId || !rangeStart || !rangeEnd) return;
    setDemandCalendarLookahead(days);
    try {
      setDemandCalendarLoading(true);
      const resp = await getBookingDemandCalendar(resolveClientId(selectedClientId), rangeStart, rangeEnd, days);
      setDemandCalendar(resp?.data?.calendar ?? null);
    } catch (e) {
      console.error("Failed to fetch demand calendar:", e);
      setDemandCalendar(null);
    } finally { setDemandCalendarLoading(false); }
  };

  /* ---- fetch sessions when client/dates/filters/sort/page change ---- */
  useEffect(() => {
    if (!effectiveClientId || !rangeStart || !rangeEnd) return;
    let cancelled = false;
    const load = async () => {
      try {
        setSessionsLoading(true);
        const params = {
          page: sessionsPag.page,
          limit: sessionsPag.limit,
          sort_by: sessionsSort.col,
          sort_dir: sessionsSort.dir,
        };
        if (outcomeFilter) params.outcome = outcomeFilter;
        if (hasContactFilter) params.has_contact = hasContactFilter;
        if (siteCatFilter) params.site_category = siteCatFilter;
        if (searchText) params.search = searchText;
        if (checkinDateFilter) params.checkin_date = checkinDateFilter;

        const resp = await getBookingSessions(resolveClientId(selectedClientId), rangeStart, rangeEnd, params);
        if (cancelled) return;
        const d = resp?.data ?? {};
        setSessions(d.sessions || []);
        setSessionsPag((p) => ({ ...p, total: d.pagination?.total || 0, total_pages: d.pagination?.total_pages || 0 }));
        if (d.site_categories?.length) setSiteCategories(d.site_categories);
      } catch (e) {
        console.error("Failed to fetch sessions:", e);
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedClientId, rangeStart, rangeEnd, sessionsPag.page, sessionsSort, outcomeFilter, hasContactFilter, siteCatFilter, searchText, checkinDateFilter, view.supabaseClientId]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setSessionsPag((p) => ({ ...p, page: 1 }));
  }, [outcomeFilter, hasContactFilter, siteCatFilter, searchText, checkinDateFilter, selectedClientId, rangeStart, rangeEnd]);

  // Auto-poll sessions every 30s (silent refresh, no loading spinner)
  useEffect(() => {
    if (!effectiveClientId || !rangeStart || !rangeEnd) return;
    const poll = setInterval(async () => {
      if (document.hidden) return; // skip the silent refresh while the tab is backgrounded
      try {
        const params = {
          page: sessionsPag.page,
          limit: sessionsPag.limit,
          sort_by: sessionsSort.col,
          sort_dir: sessionsSort.dir,
        };
        if (outcomeFilter) params.outcome = outcomeFilter;
        if (hasContactFilter) params.has_contact = hasContactFilter;
        if (siteCatFilter) params.site_category = siteCatFilter;
        if (searchText) params.search = searchText;
        if (checkinDateFilter) params.checkin_date = checkinDateFilter;

        const resp = await getBookingSessions(resolveClientId(selectedClientId), rangeStart, rangeEnd, params);
        const d = resp?.data ?? {};
        setSessions(d.sessions || []);
        setSessionsPag((p) => ({ ...p, total: d.pagination?.total || 0, total_pages: d.pagination?.total_pages || 0 }));
        if (d.site_categories?.length) setSiteCategories(d.site_categories);
      } catch {}
    }, 30000);
    return () => clearInterval(poll);
  }, [selectedClientId, rangeStart, rangeEnd, sessionsPag.page, sessionsPag.limit, sessionsSort, outcomeFilter, hasContactFilter, siteCatFilter, searchText, checkinDateFilter, view.supabaseClientId]);

  const handleSort = (col) => {
    setSessionsSort((prev) =>
      prev.col === col ? { col, dir: prev.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }
    );
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const handleAbandonedCopy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setAbandonedCopiedId(id);
      setTimeout(() => setAbandonedCopiedId(null), 1500);
    });
  };

  const handleCopyAllEmails = () => {
    const emails = [...new Set(abandonedCarts.map(c => c.visitor_email).filter(Boolean))];
    if (!emails.length) return;
    navigator.clipboard.writeText(emails.join(", ")).then(() => {
      setAbandonedCopiedAll(true);
      setTimeout(() => setAbandonedCopiedAll(false), 2000);
    });
  };

  const handleExportAbandonedCsv = () => {
    if (!abandonedCarts.length) return;
    const headers = ["Name", "Email", "Phone", "Cart Value", "Deepest Step", "Site Type", "Check-in", "Check-out", "Nights", "City", "State", "Last Activity"];
    const rows = abandonedCarts.map(c => [
      c.visitor_name || "", c.visitor_email || "", c.visitor_phone || "",
      c.abandoned_value || 0, c.deepest_step || "", c.specific_site_type || "",
      c.check_in_date_mentioned || "", c.check_out_date_mentioned || "",
      c.num_nights_mentioned || "", c.guest_city || "", c.guest_state || "",
      c.last_activity_at || "",
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `abandoned-carts-${rangeStart}-to-${rangeEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchText(searchInput);
  };

  const handleKpiClick = (outcome) => {
    setOutcomeFilter(outcome);
    setHasContactFilter("");
    setSiteCatFilter("");
    setSearchText("");
    setSearchInput("");
    setTimeout(() => {
      sessionTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  /* ---- quick filters ---- */
  const applyQuickFilter = (type) => {
    const now = new Date();
    let s, e;
    switch (type) {
      case "week": {
        const dayOfWeek = now.getDay();
        s = new Date(now); s.setDate(now.getDate() - dayOfWeek);
        e = now;
        break;
      }
      case "30d":
        e = now;
        s = new Date(now); s.setDate(s.getDate() - 30);
        break;
      case "month":
        s = startOfMonth(now);
        e = endOfMonth(now);
        break;
      case "quarter": {
        const qm = Math.floor(now.getMonth() / 3) * 3;
        s = new Date(now.getFullYear(), qm, 1);
        e = now;
        break;
      }
      default: return;
    }
    setRangeStart(fmtDate(s));
    setRangeEnd(fmtDate(e));
    setActiveQuickFilter(type);
  };

  const cur = kpis?.current;
  const prev = kpis?.previous;

  // Derive sparkline data arrays from daily trends
  const sparkSessions      = useMemo(() => trends.map(d => ({ v: d.sessions })), [trends]);
  const sparkBookings      = useMemo(() => trends.map(d => ({ v: d.bookings })), [trends]);
  const sparkConversion    = useMemo(() => trends.map(d => ({ v: d.conversion_rate ?? 0 })), [trends]);
  const sparkAbandoned     = useMemo(() => trends.map(d => ({ v: d.abandoned_value ?? 0 })), [trends]);
  const sparkLeads         = useMemo(() => trends.map(d => ({ v: d.leads ?? 0 })), [trends]);
  const sparkAvgBooking    = useMemo(() => trends.map(d => ({ v: d.avg_booking_value ?? 0 })), [trends]);

  /* ---- bookings feature gate (global project filter) ---- */
  const bookingsBlocked = !!view.selectedProjectId && !view.showBookings;
  useEffect(() => {
    if (!bookingsBlocked) return;
    // Drop stale data so it can't flash if the page re-renders before unmount.
    setKpis(null);
    setFunnel(null);
    setSessions([]);
    setAbandonedCarts([]);
    setInsights(null);
    setDemandCalendar(null);
    setBookingWindow(null);
    setPriceSensitivity(null);
    setCrossBrowse(null);
    setDayOfWeek(null);
    setRvIntelligence(null);
    setPetDemand(null);
  }, [bookingsBlocked]);

  if (bookingsBlocked) {
    return (
      <main className="flex-1 min-w-0">
        <div className="px-4 md:px-6 lg:px-10 py-12">
              <div className="max-w-xl mx-auto bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-700 mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Bookings is not enabled for this property
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {view.clientDisplayName ? `"${view.clientDisplayName}" does not have the Bookings feature enabled.` : "The selected project does not have the Bookings feature enabled."} Switch to another property in the header, or ask an admin to enable it.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(dashboardPath)}
                  className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#32B1FF] text-white text-sm font-semibold hover:bg-[#259ae6]"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          </main>
    );
  }

  /* ---- render ---- */
  return (
    <>
        {/* Main */}
        <main className="flex-1 min-w-0">
      <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">

        {/* ---- Page Header ---- */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Bookings Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Track booking activity, guest behavior, and conversion insights.</p>
        </div>

        {/* ---- Client selector + Date range (hidden when admin header controls are active) ---- */}
        {!view.supabaseClientId && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 flex flex-col sm:flex-row sm:items-center items-stretch gap-3 justify-between">
            {/* Client selector — shown for non-admin users or admins without header selection */}
            <div className="flex flex-col items-stretch gap-1.5 w-full sm:w-auto sm:flex-row sm:items-center sm:gap-3">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Viewing Property:</label>
              {clientsLoading ? (
                <div className="flex items-center gap-2 border dark:border-gray-700 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-800 w-full min-[431px]:w-auto">
                  <svg className="animate-spin h-4 w-4 text-[#32B1FF]" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  <span className="text-sm text-gray-400">Loading properties…</span>
                </div>
              ) : allowedClients.length > 1 ? (
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="border dark:border-gray-700 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium text-sm focus:ring-2 focus:ring-[#32B1FF] focus:border-transparent outline-none w-full min-[431px]:w-auto"
                >
                  {allowedClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              ) : (
                <span className="border dark:border-gray-700 rounded-lg px-4 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium w-full min-[431px]:w-auto">
                  {allowedClients[0]?.name || "No property assigned"}
                </span>
              )}
            </div>

            {/* Date range picker */}
            <DateRangePicker
              startDate={rangeStart}
              endDate={rangeEnd}
              activePreset={activeQuickFilter || "30d"}
              className="w-full min-[431px]:w-auto"
              triggerClassName="w-full min-[431px]:w-auto justify-between min-[431px]:justify-start"
              onApply={(start, end, preset) => {
                view.setDateRange(start, end, preset);
                setActiveQuickFilter(preset);
              }}
            />
          </div>
        )}

        {/* ---- Loading state while preferences load ---- */}
        {clientsLoading ? (
          <div role="status" aria-label="Loading dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from({ length: 4 }).map((_, i) => <SkeletonMetricCard key={i} />)}
            </div>
            <SkeletonChart height={300} className="rounded-xl mt-4" />
            <span className="sr-only">Loading dashboard…</span>
          </div>
        ) : (
        <>

        {/* ---- Error ---- */}
        {kpisError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-400 text-sm">
            {kpisError}
          </div>
        )}

        {/* ---- KPI Cards ---- */}
        {!kpisLoading && cur && (() => {
          const startMs = new Date(rangeStart).getTime();
          const endMs   = new Date(rangeEnd).getTime();
          const durationMs = endMs - startMs + 86400000; // +1 day so duration includes end date
          const prevStart = new Date(startMs - durationMs);
          const prevEnd   = new Date(startMs - 86400000); // day before start, no overlap
          const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          return (
            <p className="text-sm text-gray-400 dark:text-gray-500 -mb-2">
              % change vs prior period ({fmt(prevStart)} – {fmt(prevEnd)})
            </p>
          );
        })()}
        {(() => {
          const noActivity = !kpisLoading && cur && !cur.total_sessions && !prev?.total_sessions;
          const na = "No activity yet";
          return (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          <KpiCard
            title="Total Sessions"
            value={kpisLoading ? "" : noActivity ? na : fmtNum(cur?.total_sessions)}
            subtitle="Excluding bot traffic"
            variant="primary"
            icon={IconSessions}
            loading={kpisLoading}
            delta={!kpisLoading && cur && !noActivity ? <DeltaBadge current={cur.total_sessions} previous={prev?.total_sessions} /> : null}
            onClick={() => handleKpiClick("")}
            tooltip="Total unique visitor sessions during the selected period, excluding known bots and crawlers."
            sparkData={noActivity ? null : sparkSessions}
          />
          <KpiCard
            title="Bookings & Revenue"
            value={kpisLoading ? "" : noActivity ? na : <>{fmtNum(cur?.total_bookings)} <span className="text-sm font-semibold opacity-60">bookings</span> / {fmtDollar(cur?.total_revenue)} <span className="text-sm font-semibold opacity-60">revenue</span></>}
            subtitle="Completed reservations"
            variant="success"
            icon={IconBooking}
            loading={kpisLoading}
            delta={!kpisLoading && cur && !noActivity ? <DeltaBadge current={cur.total_bookings} previous={prev?.total_bookings} /> : null}
            onClick={() => handleKpiClick("booked")}
            tooltip="Number of completed reservations and their combined revenue. Revenue uses reservation value when available, otherwise site total."
            sparkData={noActivity ? null : sparkBookings}
          />
          <KpiCard
            title="Conversion Rate"
            value={kpisLoading ? "" : noActivity ? na : fmtPct(cur?.conversion_rate)}
            subtitle="Bookings / real sessions"
            variant="subtle"
            icon={IconConvert}
            loading={kpisLoading}
            delta={!kpisLoading && cur && !noActivity ? <DeltaBadge current={cur.conversion_rate} previous={prev?.conversion_rate} /> : null}
            onClick={() => handleKpiClick("booked")}
            tooltip="Percentage of sessions that resulted in a completed booking. Calculated as bookings divided by total sessions."
            sparkData={noActivity ? null : sparkConversion}
          />
          <KpiCard
            title="Abandoned Cart Value"
            value={kpisLoading ? "" : noActivity ? na : fmtDollar(cur?.abandoned_cart_value)}
            subtitle={noActivity ? "" : `${fmtNum(cur?.abandoned_cart_count || 0)} abandoned · ${fmtNum(cur?.recoverable_count || 0)} with contact info`}
            variant="warning"
            icon={IconCart}
            loading={kpisLoading}
            delta={!kpisLoading && cur && !noActivity ? <DeltaBadge current={cur.abandoned_cart_value} previous={prev?.abandoned_cart_value} /> : null}
            onClick={() => handleKpiClick("abandoned_cart")}
            tooltip="Total dollar value of carts that were started but not completed. The 'recoverable' count reflects sessions where a visitor email was captured — those appear in the Abandoned Cart Recovery section below."
            sparkData={noActivity ? null : sparkAbandoned}
          />
          <KpiCard
            title="Leads Captured"
            value={kpisLoading ? "" : noActivity ? na : fmtNum(cur?.leads_captured)}
            subtitle="Unique emails without a booking"
            variant="rose"
            icon={IconLead}
            loading={kpisLoading}
            delta={!kpisLoading && cur && !noActivity ? <DeltaBadge current={cur.leads_captured} previous={prev?.leads_captured} /> : null}
            onClick={() => handleKpiClick("lead_captured")}
            tooltip="Unique visitor emails captured from all sessions that didn't result in a booking — includes abandoned carts with contact info and other browsing sessions where an email was provided."
            sparkData={noActivity ? null : sparkLeads}
          />
          <KpiCard
            title="Avg Booking Value"
            value={kpisLoading ? "" : noActivity ? na : fmtDollar(cur?.avg_booking_value)}
            subtitle="Per booking"
            variant="muted"
            icon={IconAvg}
            loading={kpisLoading}
            delta={!kpisLoading && cur && !noActivity ? <DeltaBadge current={cur.avg_booking_value} previous={prev?.avg_booking_value} /> : null}
            onClick={() => handleKpiClick("booked")}
            tooltip="Average revenue per completed reservation. Calculated as total revenue divided by number of bookings."
            sparkData={noActivity ? null : sparkAvgBooking}
          />
        </div>
          );
        })()}

        {/* ---- Section 7: Daily Sessions & Bookings Trend ---- */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6 max-[499px]:px-0">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1 max-[499px]:px-6">Daily Sessions & Bookings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-[499px]:px-6">
            Sessions, bookings, and revenue over time.
          </p>

          {trendsLoading ? (
            <SkeletonChart height={300} className="rounded-lg" />
          ) : trends.length === 0 ? (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">No session or booking activity for this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={trends} margin={{ top: 5, right: chartLabelAngle === -30 ? 0 : 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#9ca3af", ...(chartLabelAngle !== 0 ? { angle: chartLabelAngle, textAnchor: "end" } : {}) }}
                  height={chartLabelAngle === 0 ? 30 : 50}
                  tickFormatter={(d) => {
                    const dt = new Date(d + "T00:00:00");
                    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                  }}
                  interval={trends.length > 14 ? Math.floor(trends.length / 7) - 1 : 0}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  allowDecimals={false}
                  width={chartLabelAngle === -30 ? 28 : 40}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(1) + "k" : v}`}
                  width={chartLabelAngle === -30 ? 36 : 50}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "rgba(255,255,255,0.95)",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    fontSize: "12px",
                  }}
                  labelFormatter={(d) => {
                    const dt = new Date(d + "T00:00:00");
                    return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                  }}
                  formatter={(value, name) => {
                    if (name === "revenue") return [fmtDollar(value), "Revenue"];
                    if (name === "sessions") return [fmtNum(value), "Sessions"];
                    if (name === "bookings") return [fmtNum(value), "Bookings"];
                    return [value, name];
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                  formatter={(v) => v === "sessions" ? "Sessions" : v === "bookings" ? "Bookings" : "Revenue"}
                />
                <Bar yAxisId="left" dataKey="bookings" fill="#32B1FF" opacity={0.35} radius={[2, 2, 0, 0]} barSize={trends.length > 60 ? 4 : trends.length > 30 ? 8 : 14} />
                <Line yAxisId="left" type="monotone" dataKey="sessions" stroke="#32B1FF" strokeWidth={2} dot={trends.length <= 31} activeDot={{ r: 4 }} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} strokeDasharray="5 3" />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* ---- Section 2: Booking Funnel ---- */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Booking Funnel</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Each bar shows sessions that reached that stage or beyond.
          </p>

          {funnelLoading ? (
            <SkeletonChart height={140} className="rounded-lg" />
          ) : funnel?.stages?.length ? (
            <>
              {funnel.total_sessions > (funnel.stages[0]?.count || 0) && (
                <p className="text-sm text-gray-400 dark:text-gray-500 mb-3 -mt-2">
                  {fmtNum(funnel.stages[0]?.count)} of {fmtNum(funnel.total_sessions)} sessions entered the booking flow — {fmtNum(funnel.total_sessions - (funnel.stages[0]?.count || 0))} bounced before reaching search.
                </p>
              )}
              <FunnelChart stages={funnel.stages} />
            </>
          ) : (
            <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">
              No booking funnel activity for this period.
            </div>
          )}
        </div>

        {/* ---- Section 3: Session Activity Table ---- */}
        <div id="session-activity" ref={sessionTableRef} className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Session Activity</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Every visitor session with outcome, contact, and booking details.
          </p>

          {/* ---- Active check-in date filter badge ---- */}
          {checkinDateFilter && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg">
              <span className="text-sm text-indigo-700 dark:text-indigo-300">
                Filtering by check-in date: <strong>{new Date(checkinDateFilter + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</strong>
              </span>
              <button type="button" onClick={() => setCheckinDateFilter("")}
                className="ml-1 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-800 text-indigo-500 dark:text-indigo-400 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* ---- Filters row ---- */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Outcome pills */}
            {[
              { key: "", label: "All" },
              { key: "booked", label: "Booked" },
              { key: "abandoned_cart", label: "Abandoned" },
              { key: "lead_captured", label: "Leads" },
              { key: "browsing", label: "Browsing" },
            ].map(({ key, label }) => (
              <button key={key} type="button" onClick={() => setOutcomeFilter(key)}
                className={`px-2.5 py-1.5 text-sm rounded-md font-medium transition-all min-w-[calc(50%-0.25rem)] max-[344px]:w-full max-[344px]:min-w-0 min-[431px]:min-w-[100px] min-[431px]:flex-1 min-[1600px]:flex-initial ${
                  outcomeFilter === key
                    ? "bg-[#32B1FF] text-white"
                    : "bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400"
                }`}>
                {label}
              </button>
            ))}

            {/* Divider before Contact — horizontal line at <345, hidden 345–430, vertical line at >=431 */}
            <div className="hidden max-[344px]:block w-full h-px my-2 min-[431px]:block min-[431px]:h-5 min-[431px]:w-px min-[431px]:my-0 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />

            {/* Has contact toggle */}
            <select value={hasContactFilter} onChange={(e) => setHasContactFilter(e.target.value)}
              className="border dark:border-gray-700 rounded-md px-2.5 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-[#32B1FF] min-w-[calc(50%-0.25rem)] max-[344px]:w-full max-[344px]:min-w-0 min-[431px]:min-w-[100px] min-[431px]:flex-1 min-[1600px]:flex-initial">
              <option value="">Contact: Any</option>
              <option value="true">Has Contact</option>
              <option value="false">No Contact</option>
            </select>

            {/* Site category dropdown */}
            {siteCategories.length > 0 && (
              <select value={siteCatFilter} onChange={(e) => setSiteCatFilter(e.target.value)}
                className="border dark:border-gray-700 rounded-md px-2.5 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-[#32B1FF] max-[430px]:w-full min-w-[calc(50%-0.25rem)] min-[431px]:min-w-[100px] min-[431px]:flex-1 min-[1600px]:flex-initial">
                <option value="">All Site Types</option>
                {siteCategories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}

            {/* Divider after Site Types — only at <345 */}
            <div className="hidden max-[344px]:block w-full h-px my-2 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />

            {/* Force wrap below 1600 — search + columns + save view drop to a new row together */}
            <div className="hidden min-[431px]:block min-[1600px]:hidden basis-full h-0" aria-hidden="true" />

            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-1 w-full min-[431px]:w-auto min-[431px]:flex-1 min-[345px]:flex-nowrap min-[1600px]:flex-initial min-[1600px]:ml-auto">
              <input
                type="text" placeholder="Search name, email, site..." value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="border dark:border-gray-700 rounded-md px-2.5 py-1.5 text-sm w-full max-[344px]:flex-none min-[345px]:flex-1 min-w-0 min-[1600px]:flex-none min-[1600px]:w-44 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-1 focus:ring-[#32B1FF]"
              />
              <button type="submit" className="max-[344px]:w-full px-2.5 py-1.5 text-sm rounded-md bg-[#32B1FF] text-white hover:bg-blue-600 transition-all">
                Search
              </button>
              {searchText && (
                <button type="button" onClick={() => { setSearchInput(""); setSearchText(""); }}
                  className="max-[344px]:w-full px-2 py-1.5 text-sm rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all">
                  Clear
                </button>
              )}
            </form>

            {canCustomColumns && (
              <>
                {/* Divider before Columns — only at <345 (also forces the row wrap) */}
                <div className="hidden max-[344px]:block w-full h-px my-2 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />

                {/* Wrap break — push Columns + Save View to a new row at <665 (skipped at <345 where the divider above already wraps) */}
                <div className="basis-full h-0 max-[344px]:hidden min-[665px]:hidden" aria-hidden="true" />

                {/* Divider */}
                <div className="hidden min-[665px]:block h-5 w-px bg-gray-200 dark:bg-gray-700 ml-2" />

                {/* Choose Columns */}
                <div className="relative w-[calc(50%-0.25rem)] max-[344px]:w-full flex-none min-[665px]:w-[150px] min-[1600px]:w-auto" ref={columnPickerRef}>
                  <button type="button" onClick={() => setShowColumnPicker(!showColumnPicker)}
                    className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all w-full min-[1600px]:w-auto">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Columns
                  </button>
                  {showColumnPicker && (() => {
                    const isNarrowPicker = chartLabelAngle === -30; // <500px
                    const orderedColumns = isNarrowPicker
                      ? [
                          ...SESSION_COLUMNS.filter((_, i) => i % 2 === 0),
                          ...SESSION_COLUMNS.filter((_, i) => i % 2 === 1),
                        ]
                      : SESSION_COLUMNS;
                    return (
                    <div className="absolute top-full mt-1 max-[664px]:left-0 min-[665px]:right-0 w-[420px] max-[499px]:w-[250px] max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg shadow-xl z-30 p-3">
                      <div className="grid grid-cols-1 min-[500px]:grid-cols-2 gap-x-4 gap-y-0.5">
                        {orderedColumns.map((col) => (
                          <label key={col.key}
                            className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer ${col.locked ? "opacity-70 cursor-not-allowed bg-gray-100 dark:bg-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-800"}`}>
                            <input
                              type="checkbox"
                              checked={isColVisible(col.key)}
                              disabled={col.locked}
                              onChange={() => !col.locked && toggleColumn(col.key)}
                              className="w-3.5 h-3.5 rounded border-gray-300 text-[#32B1FF] focus:ring-[#32B1FF]"
                            />
                            <span className="text-gray-700 dark:text-gray-300">{col.label}</span>
                            {col.locked && <span className="text-sm text-gray-400 ml-auto">Required</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                    );
                  })()}
                </div>

                {/* Save View */}
                <button type="button" onClick={handleSaveView} disabled={savingView}
                  className="inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-sm rounded-md bg-[#32B1FF] text-white hover:bg-blue-600 disabled:opacity-50 transition-all w-[calc(50%-0.25rem)] max-[344px]:w-full flex-none min-[665px]:w-[150px] min-[1600px]:w-auto">
                  {savingView ? "Saving..." : savedViewMsg || "Save View"}
                </button>
              </>
            )}
          </div>

          {/* ---- Table ---- */}
          <div className="responsive-session-table-wrapper overflow-x-auto rounded-lg border dark:border-gray-800">
            <table className="responsive-session-table w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/60 border-b dark:border-gray-700">
                  {SESSION_COLUMNS.filter(c => isColVisible(c.key)).map((col) => (
                    <th key={col.key}
                      className={`${col.width} px-3 py-2.5 text-left text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide ${col.sortKey ? "cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200" : ""}`}
                      onClick={col.sortKey ? () => handleSort(col.sortKey) : undefined}>
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {col.sortKey && sessionsSort.col === col.sortKey && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            {sessionsSort.dir === "asc"
                              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                              : <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />}
                          </svg>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y dark:divide-gray-800 transition-opacity ${sessionsLoading && sessions.length > 0 ? "opacity-50" : ""}`}>
                {sessionsLoading && sessions.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="py-12 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-[#32B1FF]" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-gray-500 dark:text-gray-400">Loading sessions...</span>
                      </div>
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="py-12 text-center text-gray-400 dark:text-gray-500">
                      No visitor sessions match the current filters. Try adjusting the date range or clearing filters.
                    </td>
                  </tr>
                ) : sessions.map((s) => {
                  const oc = OUTCOME_CONFIG[s.outcome] || OUTCOME_CONFIG.browsing;
                  const contact = s.visitor_email || s.visitor_phone || null;
                  const guestCount = (s.num_adults_mentioned || 0) + (s.num_children_mentioned || 0);
                  const checkInDisplay = s.check_in_date_mentioned && s.check_out_date_mentioned
                    ? `${new Date(s.check_in_date_mentioned).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(s.check_out_date_mentioned).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                    : s.check_in_date_mentioned
                    ? new Date(s.check_in_date_mentioned).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                    : null;

                  const isLive = s.last_activity_at && (Date.now() - new Date(s.last_activity_at).getTime()) < 15 * 60 * 1000;
                  const cellMap = {
                    first_seen_at: (
                      <td key="first_seen_at" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap" title={fullTimestamp(s.first_seen_at)}>
                        <span className="inline-flex items-center gap-1.5">
                          {isLive && (
                            <span className="relative flex h-2 w-2 shrink-0" title="Active in last 15 min">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                          )}
                          {relativeTime(s.first_seen_at)}
                        </span>
                      </td>
                    ),
                    visitor_name: (
                      <td key="visitor_name" className="px-3 py-2.5 font-medium whitespace-nowrap truncate max-w-[140px]">
                        {canViewSessionDetails ? (
                          <button type="button" onClick={() => { setSelectedSessionId(s.id); setSessionPanelOpen(true); }}
                            className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer truncate text-left">
                            {s.visitor_name || "Anonymous"}
                          </button>
                        ) : (
                          <span className="text-gray-800 dark:text-gray-200 truncate">{s.visitor_name || "Anonymous"}</span>
                        )}
                      </td>
                    ),
                    contact: (
                      <td key="contact" className="px-3 py-2.5 whitespace-nowrap">
                        {contact ? (
                          <button type="button" onClick={() => handleCopy(contact, s.id)}
                            className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[160px]"
                            title={`Click to copy: ${contact}`}>
                            <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="9" y="9" width="13" height="13" rx="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            <span className="truncate">{copiedId === s.id ? "Copied!" : contact}</span>
                          </button>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                      </td>
                    ),
                    check_in: (
                      <td key="check_in" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {checkInDisplay || "—"}
                      </td>
                    ),
                    guests: (
                      <td key="guests" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {guestCount > 0 ? guestCount : "—"}
                      </td>
                    ),
                    nights: (
                      <td key="nights" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.num_nights_mentioned > 0 ? s.num_nights_mentioned : "—"}
                      </td>
                    ),
                    site_category: (
                      <td key="site_category" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.site_category || "—"}
                      </td>
                    ),
                    specific_site_type: (
                      <td key="specific_site_type" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[140px]" title={s.specific_site_type}>
                        {s.specific_site_type || "—"}
                      </td>
                    ),
                    deepest_step: (
                      <td key="deepest_step" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {STEP_LABELS[s.deepest_step] || s.deepest_step || "—"}
                      </td>
                    ),
                    outcome: (
                      <td key="outcome" className="px-3 py-2.5">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-sm font-semibold ${oc.bg} ${oc.text}`}>
                          {oc.label}
                        </span>
                      </td>
                    ),
                    site_total: (
                      <td key="site_total" className="px-3 py-2.5 text-gray-800 dark:text-gray-200 font-medium whitespace-nowrap">
                        {s.value > 0 ? fmtDollar(s.value) : "—"}
                      </td>
                    ),
                    nightly_rate: (
                      <td key="nightly_rate" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.nightly_rate > 0 ? fmtDollar(s.nightly_rate) : "—"}
                      </td>
                    ),
                    location: (
                      <td key="location" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[140px]" title={[s.guest_city, s.guest_state].filter(Boolean).join(", ")}>
                        {s.guest_city || s.guest_state ? [s.guest_city, s.guest_state].filter(Boolean).join(", ") : "—"}
                      </td>
                    ),
                    pets: (
                      <td key="pets" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.num_pets_mentioned > 0 ? s.num_pets_mentioned : "—"}
                      </td>
                    ),
                    rv_type: (
                      <td key="rv_type" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[120px]" title={s.rv_type_mentioned}>
                        {s.rv_type_mentioned || "—"}
                      </td>
                    ),
                    referral_source: (
                      <td key="referral_source" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[120px]" title={s.referral_source}>
                        {s.referral_source || "—"}
                      </td>
                    ),
                    booking_window: (
                      <td key="booking_window" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.booking_window_days > 0 ? `${s.booking_window_days}d` : "—"}
                      </td>
                    ),
                    rv_length: (
                      <td key="rv_length" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.rv_length_mentioned > 0 ? `${s.rv_length_mentioned} ft` : "—"}
                      </td>
                    ),
                    equipment: (
                      <td key="equipment" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[140px]" title={[s.equipment_make, s.equipment_model].filter(Boolean).join(" ")}>
                        {s.equipment_make ? [s.equipment_make, s.equipment_model].filter(Boolean).join(" ") : "—"}
                      </td>
                    ),
                    site_number: (
                      <td key="site_number" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.site_number || "—"}
                      </td>
                    ),
                    last_activity: (
                      <td key="last_activity" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap" title={fullTimestamp(s.last_activity_at)}>
                        {relativeTime(s.last_activity_at)}
                      </td>
                    ),
                    email: (
                      <td key="email" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 truncate max-w-[180px]" title={s.visitor_email}>
                        {s.visitor_email || "—"}
                      </td>
                    ),
                    phone: (
                      <td key="phone" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.visitor_phone || "—"}
                      </td>
                    ),
                    device: (
                      <td key="device" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.device || "—"}
                      </td>
                    ),
                    check_out: (
                      <td key="check_out" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {s.check_out_date_mentioned ? new Date(s.check_out_date_mentioned).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                      </td>
                    ),
                    transaction_id: (
                      <td key="transaction_id" className="px-3 py-2.5 text-gray-600 dark:text-gray-400 font-mono truncate max-w-[140px]" title={s.transaction_id}>
                        {s.transaction_id || "—"}
                      </td>
                    ),
                  };

                  return (
                    <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                      {SESSION_COLUMNS.filter(c => isColVisible(c.key)).map(c =>
                        React.cloneElement(cellMap[c.key], { "data-label": c.label })
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ---- Pagination ---- */}
          {sessionsPag.total > 0 && (
            <div className="flex flex-col gap-3 min-[550px]:flex-row min-[550px]:items-center min-[550px]:justify-between mt-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Showing {((sessionsPag.page - 1) * sessionsPag.limit) + 1}–{Math.min(sessionsPag.page * sessionsPag.limit, sessionsPag.total)} of {sessionsPag.total.toLocaleString()} sessions
              </span>
              <div className="flex flex-wrap items-center gap-1 max-[549px]:w-full max-[549px]:justify-between">
                <button type="button" disabled={sessionsPag.page <= 1 || sessionsLoading}
                  onClick={() => setSessionsPag((p) => ({ ...p, page: p.page - 1 }))}
                  className="max-[639px]:min-w-[100px] px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  Previous
                </button>

                {/* Page numbers (hidden below 640px — Previous/Next only on small screens) */}
                <div className="hidden min-[640px]:flex flex-wrap items-center gap-1">
                  {(() => {
                    const pages = [];
                    const tp = sessionsPag.total_pages;
                    const cp = sessionsPag.page;
                    const delta = 2;
                    let start = Math.max(1, cp - delta);
                    let end = Math.min(tp, cp + delta);
                    if (start > 1) { pages.push(1); if (start > 2) pages.push("..."); }
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < tp) { if (end < tp - 1) pages.push("..."); pages.push(tp); }
                    return pages.map((p, idx) =>
                      p === "..." ? (
                        <span key={`e${idx}`} className="px-1.5 text-sm text-gray-400">...</span>
                      ) : (
                        <button key={p} type="button" disabled={sessionsLoading} onClick={() => setSessionsPag((prev) => ({ ...prev, page: p }))}
                          className={`w-7 h-7 text-sm rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                            p === cp
                              ? "bg-[#32B1FF] text-white font-bold"
                              : "bg-white dark:bg-gray-800 border dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}>
                          {p}
                        </button>
                      )
                    );
                  })()}
                </div>

                <button type="button" disabled={sessionsPag.page >= sessionsPag.total_pages || sessionsLoading}
                  onClick={() => setSessionsPag((p) => ({ ...p, page: p.page + 1 }))}
                  className="max-[639px]:min-w-[100px] px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ---- Section 5: Abandoned Cart Recovery ---- */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
          {/* Row 1: Title + stat badge + recovery link */}
          <div className="flex flex-wrap items-center justify-between gap-y-2 mb-1">
            <div className="flex flex-col min-[750px]:flex-row min-[750px]:items-center items-start gap-1 min-[750px]:gap-3">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Abandoned Cart Recovery</h2>
              {!abandonedLoading && abandonedCarts.length > 0 && (
                <span className="text-sm font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-0.5 rounded-full">
                  {abandonedCarts.length} recoverable · {fmtDollar(abandonedCarts.reduce((s, c) => s + (c.abandoned_value || 0), 0))}
                </span>
              )}
            </div>
            {canSendCartEmail && (
              <button type="button"
                onClick={() => setRecoveryPanelOpen(true)}
                className="max-[499px]:w-full max-[499px]:justify-center inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 border border-emerald-200 dark:border-emerald-800 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Recovery Dashboard
              </button>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Recoverable sessions with captured contact info that abandoned at cart or later.
          </p>

          {/* Row 2: Sort + Actions */}
          {!abandonedLoading && abandonedCarts.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 gap-y-2 min-[650px]:justify-between mb-4">
              <div className="flex items-center gap-1.5 max-[649px]:w-full">
                {[
                  { key: "newest", label: "Newest" },
                  { key: "value", label: "Highest Value" },
                ].map(({ key, label }) => (
                  <button key={key} type="button"
                    onClick={() => { setAbandonedSort(key); setAbandonedPage(1); }}
                    className={`max-[649px]:w-[30%] max-[499px]:w-[calc(50%-0.25rem)] max-[649px]:flex max-[649px]:items-center max-[649px]:justify-center px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                      abandonedSort === key
                        ? "bg-[#32B1FF] text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 max-[649px]:w-full">
                <button type="button" onClick={handleCopyAllEmails}
                  className="max-[649px]:w-[30%] max-[499px]:w-[calc(50%-0.25rem)] max-[649px]:justify-center inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  {abandonedCopiedAll ? (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                      Copy Emails
                    </>
                  )}
                </button>
                <button type="button" onClick={handleExportAbandonedCsv}
                  className="max-[649px]:w-[30%] max-[499px]:w-[calc(50%-0.25rem)] max-[649px]:justify-center inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Export
                </button>
                {canSendCartEmail && (
                  <>
                    <div className="h-4 w-px bg-gray-200 dark:bg-gray-700" />
                    {recoveryConfig && !recoveryConfig.can_enable_auto_send && !autoEmailEnabled ? (
                      <span className="text-sm text-gray-400 dark:text-gray-500 italic"
                        title={!recoveryConfig.postmark_sender_verified
                          ? "Sender domain must be verified before auto-send can be enabled. Contact your Insider Perks account manager."
                          : !recoveryConfig.auto_recovery_sender_email
                          ? "Sender email must be configured first."
                          : "Recovery email sequences must be configured first."}>
                        Auto Send unavailable
                      </span>
                    ) : (
                      <label className="inline-flex items-center gap-1.5 cursor-pointer" title="Automatically send recovery emails based on configured sequences">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Auto Send</span>
                        <div className="relative">
                          <input type="checkbox" checked={autoEmailEnabled} onChange={handleToggleAutoEmail} disabled={savingAutoEmail} className="sr-only peer" />
                          <div className="w-8 h-4 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-[#32B1FF] transition-colors" />
                          <div className="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                        </div>
                      </label>
                    )}
                    {autoSendError && (
                      <span className="text-sm text-red-600 dark:text-red-400">{autoSendError}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {abandonedLoading ? (
            <SkeletonChart height={160} className="rounded-lg" />
          ) : abandonedCarts.length === 0 ? (
            <div className="text-center py-10 text-gray-400 dark:text-gray-500 text-sm">
              No abandoned carts for this period — all visitors completed their bookings or didn't reach the cart.
            </div>
          ) : (() => {
            const sorted = [...abandonedCarts].sort((a, b) => {
              if (abandonedSort === "value") return (b.abandoned_value || 0) - (a.abandoned_value || 0);
              return new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0);
            });
            const totalAbandoned = sorted.length;
            const totalAbandonedPages = Math.ceil(totalAbandoned / ABANDONED_PER_PAGE);
            const startIdx = (abandonedPage - 1) * ABANDONED_PER_PAGE;
            const pagedCarts = sorted.slice(startIdx, startIdx + ABANDONED_PER_PAGE);

            return (
              <>
                <div className="overflow-x-auto">
                  <div className="space-y-2 min-[1200px]:min-w-[760px]">
                    {pagedCarts.map((c) => {
                    const stepLabel = {
                      cart: "Added to cart",
                      checkout: "Started checkout",
                      checkout_contact: "Entered contact info",
                      checkout_payment: "Reached payment",
                    }[c.deepest_step] || c.deepest_step;

                    const dates = c.check_in_date_mentioned && c.check_out_date_mentioned
                      ? `${new Date(c.check_in_date_mentioned).toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${new Date(c.check_out_date_mentioned).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
                      : null;

                    const location = [c.guest_city, c.guest_state].filter(Boolean).join(", ");

                    return (
                      <div key={c.id} className="flex flex-wrap items-center gap-3 gap-y-2 px-4 py-2.5 rounded-lg border dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                        {/* Value badge */}
                        <div className="shrink-0 w-22 text-center max-[719px]:order-1">
                          <div className="text-sm font-extrabold text-amber-600 dark:text-amber-400">
                            {fmtDollar(c.abandoned_value)}
                          </div>
                          <div className="text-sm text-gray-400 dark:text-gray-500 leading-tight">{stepLabel}</div>
                        </div>

                        {/* Visitor info */}
                        <div className="flex-1 min-w-0 max-[719px]:order-3 max-[719px]:basis-full max-[724px]:bg-gray-50 dark:max-[724px]:bg-gray-800/40 max-[724px]:rounded-lg max-[724px]:px-3 max-[724px]:py-2">
                          <div className="flex flex-col max-[399px]:items-start min-[400px]:flex-row min-[400px]:items-center gap-y-0 min-[400px]:gap-2">
                            <span className="font-semibold text-sm text-gray-800 dark:text-gray-200 truncate">
                              {c.visitor_name || "Anonymous"}
                            </span>
                            {location && (
                              <span className="text-sm text-gray-400 dark:text-gray-500">
                                {location}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                            {c.visitor_email}
                            {c.visitor_phone && (
                              <span className="hidden min-[450px]:inline"> · {c.visitor_phone}</span>
                            )}
                          </div>
                          {c.visitor_phone && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate min-[450px]:hidden">
                              {c.visitor_phone}
                            </div>
                          )}
                        </div>

                        {/* Booking intent — always visible; wraps to its own row (left-aligned, inline " · " separated) below 1025, then stacks at ≥1025 */}
                        <div className="flex flex-col items-end text-right shrink-0 max-[1024px]:basis-full max-[1024px]:flex-row max-[1024px]:flex-wrap max-[1024px]:items-center max-[1024px]:text-left max-[1024px]:gap-x-1 max-[719px]:order-4 min-[720px]:max-[1024px]:order-1">
                          {dates && (
                            <span className="text-sm text-gray-600 dark:text-gray-400">{dates}</span>
                          )}
                          {dates && (c.num_nights_mentioned || c.specific_site_type) && (
                            <span className="hidden max-[1024px]:inline text-sm text-gray-400 dark:text-gray-500" aria-hidden="true">·</span>
                          )}
                          <span className="text-sm text-gray-400 dark:text-gray-500">
                            {[
                              c.num_nights_mentioned ? `${c.num_nights_mentioned} nights` : null,
                              c.specific_site_type || null,
                            ].filter(Boolean).join(" · ") || "—"}
                          </span>
                        </div>

                        {/* Time since abandon */}
                        <div className="shrink-0 w-20 text-right ml-auto min-[1200px]:ml-0 max-[719px]:order-2">
                          <span className="text-sm text-gray-500 dark:text-gray-400" title={fullTimestamp(c.last_activity_at)}>{relativeTime(c.last_activity_at)}</span>
                        </div>

                        {/* Wrap break — push action buttons to a new row at 1025–1199 (at <720, Visitor's basis-full breaks the row; at 720–1024, Booking intent's basis-full does it) */}
                        <div className="hidden min-[1025px]:block min-[1200px]:hidden basis-full h-0" aria-hidden="true" />

                        {/* Copy email action */}
                        <button
                          type="button"
                          onClick={() => handleAbandonedCopy(c.visitor_email, c.id)}
                          className="max-[1199px]:w-[30%] max-[1199px]:ml-auto max-[529px]:w-[calc(50%-0.5rem)] max-[374px]:w-full shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 border border-amber-200 dark:border-amber-800 transition-all max-[719px]:order-5 min-[720px]:max-[1024px]:order-2"
                          title={`Copy: ${c.visitor_email}`}
                        >
                          {abandonedCopiedId === c.id ? (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              Copied!
                            </>
                          ) : (
                            <>
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                              Copy Email
                            </>
                          )}
                        </button>

                        {/* Send Email / Email Sent badge — 2026-08-13: now shows
                            the recovery status (Sent / Opened / Clicked / Converted)
                            instead of just "Email Sent", since getCartEmailStatus
                            returns the full recovery_actions lifecycle. */}
                        {canSendCartEmail && (
                          (() => {
                            const es = emailStatuses[(c.visitor_email || '').toLowerCase().trim()];
                            if (!es) {
                              return (
                                <button
                                  type="button"
                                  onClick={() => { setEmailModalCart(c); setEmailModalOpen(true); }}
                                  className="max-[374px]:w-full shrink-0 inline-flex items-center justify-center gap-1.5 w-[110px] max-[529px]:w-[calc(50%-0.5rem)] px-3 py-1.5 text-sm font-medium rounded-md bg-[#32B1FF]/10 text-[#32B1FF] hover:bg-[#32B1FF]/20 border border-[#32B1FF]/30 transition-all max-[719px]:order-6 min-[720px]:max-[1024px]:order-2"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  Send Email
                                </button>
                              );
                            }
                            // Status hierarchy: converted > clicked > opened > delivered > sent > bounced/failed
                            const st = es.status || 'sent';
                            const label = st === 'converted' ? 'Converted'
                              : st === 'clicked' ? 'Clicked'
                              : st === 'opened' ? 'Opened'
                              : st === 'delivered' ? 'Delivered'
                              : st === 'bounced' ? 'Bounced'
                              : st === 'failed' ? 'Failed'
                              : 'Email Sent';
                            const color = st === 'converted' ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                              : st === 'clicked' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800'
                              : st === 'opened' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                              : st === 'bounced' || st === 'failed' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
                              : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800';
                            return (
                              <span className={`max-[374px]:w-full shrink-0 inline-flex items-center justify-center gap-1 w-[110px] max-[529px]:w-[calc(50%-0.5rem)] px-3 py-1.5 text-sm font-medium rounded-md border ${color} max-[719px]:order-6 min-[720px]:max-[1024px]:order-2`}
                                title={`Status: ${st}${es.sent_at ? ` · Sent ${new Date(es.sent_at).toLocaleString()}` : ''}`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                {label}
                              </span>
                            );
                          })()
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>

                {/* Pagination */}
                {totalAbandonedPages > 1 && (
                  <div className="flex flex-col gap-3 min-[550px]:flex-row min-[550px]:items-center min-[550px]:justify-between mt-4 pt-3 border-t dark:border-gray-800">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {startIdx + 1}–{Math.min(startIdx + ABANDONED_PER_PAGE, totalAbandoned)} of {totalAbandoned}
                    </span>
                    <div className="flex items-center gap-1.5 max-[549px]:w-full max-[549px]:justify-between">
                      <button type="button" disabled={abandonedPage <= 1}
                        onClick={() => setAbandonedPage((p) => p - 1)}
                        className="max-[639px]:min-w-[100px] px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                        Previous
                      </button>

                      {/* Page numbers (hidden below 640px — Previous/Next only on small screens) */}
                      <div className="hidden min-[640px]:flex items-center gap-1.5">
                        {(() => {
                          const pages = [];
                          const tp = totalAbandonedPages;
                          const cp = abandonedPage;
                          const d = 2;
                          let start = Math.max(1, cp - d);
                          let end = Math.min(tp, cp + d);
                          if (start > 1) { pages.push(1); if (start > 2) pages.push("..."); }
                          for (let i = start; i <= end; i++) pages.push(i);
                          if (end < tp) { if (end < tp - 1) pages.push("..."); pages.push(tp); }
                          return pages.map((p, idx) =>
                            p === "..." ? (
                              <span key={`ae${idx}`} className="px-1.5 text-sm text-gray-400">...</span>
                            ) : (
                              <button key={p} type="button" onClick={() => setAbandonedPage(p)}
                                className={`w-7 h-7 text-sm rounded-md transition-all ${
                                  p === cp
                                    ? "bg-[#32B1FF] text-white font-bold"
                                    : "bg-white dark:bg-gray-800 border dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}>
                                {p}
                              </button>
                            )
                          );
                        })()}
                      </div>

                      <button type="button" disabled={abandonedPage >= totalAbandonedPages}
                        onClick={() => setAbandonedPage((p) => p + 1)}
                        className="max-[639px]:min-w-[100px] px-2.5 py-1.5 text-sm rounded-md border dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* ---- Section 6: Demand & Traffic Insights ---- */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Demand & Traffic Insights</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Patterns in site demand, guest origins, booking behavior, and traffic sources.
          </p>

          {insightsLoading ? (
            <SkeletonChart height={300} className="rounded-lg" />
          ) : !insights ? (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">Not enough session data to generate insights for this period.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Card 1: Top Site Types */}
              <InsightCard title="Top Site Types in Demand" subtitle="Which accommodations attract attention vs. convert" exportData={insights.site_types}>
                {insights.site_types?.length ? (
                  <div className="space-y-2">
                    <div className="hidden min-[550px]:flex items-center gap-3 text-sm uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
                      <div className="w-24 shrink-0">Type</div>
                      <div className="flex-1 min-w-0" />
                      <span className="w-20 text-right shrink-0">Views/Bkd</span>
                      <span className="w-14 text-right shrink-0">Conv</span>
                      <span className="w-16 text-right shrink-0">Rate</span>
                    </div>
                    {insights.site_types.slice(0, 8).map((s, idx) => {
                      const maxViews = insights.site_types[0]?.views || 1;
                      const isEven = idx % 2 === 0;
                      const typeLabel = displayLabel(s.type);
                      return (
                        <div key={s.type} className={`flex flex-wrap items-center gap-3 gap-y-1 max-[549px]:gap-x-1.5 max-[549px]:rounded-md max-[549px]:px-2 max-[549px]:py-2 ${isEven ? "max-[549px]:bg-gray-50 dark:max-[549px]:bg-gray-800/40" : ""}`}>
                          <div className="w-24 text-sm font-medium text-gray-700 dark:text-gray-300 truncate shrink-0 max-[549px]:w-full max-[549px]:flex-none" title={typeLabel}>{typeLabel}</div>
                          <div className="flex-1 min-w-0 bg-gray-100 dark:bg-gray-800 rounded-full h-3.5 relative overflow-hidden max-[549px]:basis-full">
                            <div className="h-full rounded-full bg-[#32B1FF]/30" style={{ width: `${(s.views / maxViews) * 100}%` }} />
                            <div className="absolute top-0 left-0 h-full rounded-full bg-[#32B1FF]" style={{ width: `${(s.bookings / maxViews) * 100}%` }} />
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400 w-20 text-right shrink-0 max-[549px]:w-auto whitespace-nowrap">
                            <span className="hidden max-[549px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-0.5">Views/Bkd</span>
                            {s.views} / {s.bookings}
                          </span>
                          <span className={`text-sm font-semibold w-14 text-right shrink-0 max-[549px]:w-auto whitespace-nowrap ${s.conversion_pct > 5 ? "text-emerald-600 dark:text-emerald-400" : "text-gray-500 dark:text-gray-400"}`}>
                            <span className="hidden max-[549px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-0.5">Conv</span>
                            {s.conversion_pct}%
                          </span>
                          <span className="text-sm text-gray-400 dark:text-gray-500 w-16 text-right shrink-0 max-[549px]:w-auto whitespace-nowrap max-[399px]:basis-full max-[399px]:text-left">
                            <span className="hidden max-[549px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-0.5">Rate</span>
                            {s.avg_rate != null ? `$${s.avg_rate}` : "—"}
                          </span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-3 mt-1 pt-2 border-t dark:border-gray-700">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-[#32B1FF]/30" /><span className="text-sm text-gray-400">Views</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-[#32B1FF]" /><span className="text-sm text-gray-400">Bookings</span></div>
                    </div>
                  </div>
                ) : <EmptyInsight message="No site type data for this period." />}
              </InsightCard>

              {/* Card 2: Guest Origins — Compact Map */}
              <InsightCard title="Where Are Guests Coming From?" subtitle="Top states by session volume" exportData={insights.guest_origins}>
                {insights.guest_origins?.length ? (() => {
                  const maxSess = insights.guest_origins[0]?.sessions || 1;
                  const stateMap = {};
                  insights.guest_origins.forEach((g) => { stateMap[g.state.toLowerCase()] = g; });
                  return (
                    <GuestOriginsMap stateMap={stateMap} maxSess={maxSess} origins={insights.guest_origins} />
                  );
                })() : <EmptyInsight message="No guest location data for this period." />}
              </InsightCard>

              {/* Traffic Sources (kept in this section, hidden for Insider Perks RV) */}
              {!isInsiderPerks && <InsightCard title="Traffic Sources" subtitle="Where visitors came from (Based on traffic source data)" exportData={insights.traffic_sources}>
                {insights.traffic_sources?.length ? (
                  <div className="space-y-1.5">
                    <div className="hidden min-[500px]:flex items-center gap-2 text-sm uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
                      <span className="w-44 shrink-0">Source</span>
                      <div className="flex-1" />
                      <span className="w-8 text-right shrink-0">Sess</span>
                      <span className="w-8 text-right shrink-0">Bkd</span>
                    </div>
                    {insights.traffic_sources.slice(0, 10).map((t, idx) => {
                      const maxS = insights.traffic_sources[0]?.sessions || 1;
                      const isEven = idx % 2 === 0;
                      const sourceLabel = displayLabel(t.source);
                      return (
                        <div key={t.source} className={`flex flex-wrap items-center gap-2 gap-y-1 max-[499px]:rounded-md max-[499px]:px-2 max-[499px]:py-2 ${isEven ? "max-[499px]:bg-gray-50 dark:max-[499px]:bg-gray-800/40" : ""}`}>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-44 shrink-0 break-all leading-tight max-[499px]:w-full max-[499px]:flex-none">{sourceLabel}</span>
                          <div className="flex-1 min-w-0 bg-gray-100 dark:bg-gray-800 rounded-full h-3 overflow-hidden max-[499px]:basis-full">
                            <div className="h-full rounded-full bg-amber-400 dark:bg-amber-500" style={{ width: `${(t.sessions / maxS) * 100}%` }} />
                          </div>
                          <span className="text-sm text-gray-500 dark:text-gray-400 w-8 text-right shrink-0 max-[499px]:w-auto">
                            <span className="hidden max-[499px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-1">Sess</span>
                            {t.sessions}
                          </span>
                          <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400 w-8 text-right shrink-0 max-[499px]:w-auto">
                            <span className="hidden max-[499px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-1">Bkd</span>
                            {t.bookings}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : <EmptyInsight message="No traffic source data for this period." />}
              </InsightCard>}

            </div>
          )}
        </div>

        {/* ---- Section 7: Demand Forecast (NEW) ---- */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Demand Forecast</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            What dates guests are searching for and when they plan ahead.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3">
              <InsightCard title="Demand Calendar" subtitle="Check-in dates guests are searching for. Darker cells indicate higher demand." exportData={demandCalendar}>
                <DemandCalendarPanel
                  data={demandCalendar}
                  loading={demandCalendarLoading}
                  onLookaheadChange={handleDemandCalendarLookahead}
                  startDate={rangeStart}
                  onCellClick={(date) => {
                    setCheckinDateFilter(date);
                    setSessionsPag((p) => ({ ...p, page: 1 }));
                    document.getElementById("session-activity")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  activeDate={checkinDateFilter}
                />
              </InsightCard>
            </div>
            <div className="lg:col-span-2">
              <InsightCard title="Booking Window" subtitle="How far ahead guests search, broken down by site type" exportData={bookingWindow}>
                <BookingWindowPanel
                  data={bookingWindow}
                  loading={bookingWindowLoading}
                />
              </InsightCard>
            </div>
          </div>
        </div>

        {/* ---- Section 8: Conversion Intelligence (NEW) ---- */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Conversion Intelligence</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Where pricing and browsing patterns affect bookings.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <InsightCard title="Price Sensitivity" subtitle="Conversion rate by price range — identifies where higher rates may be discouraging bookings." exportData={priceSensitivity}>
              <PriceSensitivityPanel
                data={priceSensitivity}
                loading={priceSensitivityLoading}
              />
            </InsightCard>
            <InsightCard title="Cross-Browse Patterns" subtitle="Site types frequently viewed together in the same session, showing which accommodations guests weigh against each other." exportData={crossBrowse?.pairs}>
              <CrossBrowsePanel
                summary={crossBrowse?.summary}
                pairs={crossBrowse?.pairs}
                loading={crossBrowseLoading}
              />
            </InsightCard>
          </div>
        </div>

        {/* ---- Section 9: Behavioral Breakdowns (MODIFIED) ---- */}
        <div>
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-1">Behavioral Breakdowns</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Guest search patterns, timing preferences, and niche demand like RVs and pets.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* Stay Length (existing — moved from insights) */}
            {insightsLoading ? (
              <InsightCard title="Stay Length Distribution" subtitle="How long are guests looking to stay">
                <div className="animate-pulse h-32 bg-gray-800/40 rounded" />
              </InsightCard>
            ) : insights?.stay_length?.length ? (
              <InsightCard title="Stay Length Distribution" subtitle="How long are guests looking to stay" exportData={insights.stay_length}>
                {(() => {
                  const maxSess = Math.max(...insights.stay_length.map((x) => x.sessions), 1);
                  return (
                    <div className="space-y-2.5">
                      {insights.stay_length.map((s, idx) => (
                        <div key={s.bucket} className={`max-[499px]:rounded-md max-[499px]:px-2 max-[499px]:py-2 ${idx % 2 === 0 ? "max-[499px]:bg-gray-50 dark:max-[499px]:bg-gray-800/40" : ""}`}>
                          <div className="flex items-center justify-between mb-1 max-[499px]:flex-col max-[499px]:items-start max-[499px]:gap-0.5">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{s.bucket}</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {s.sessions} {s.sessions === 1 ? "session" : "sessions"} · {s.bookings} booked
                              {` · ${s.avg_rate != null ? `$${s.avg_rate}/night` : "no rate data"}`}
                            </span>
                          </div>
                          <div className="bg-gray-100 dark:bg-gray-800 rounded-full h-4 overflow-hidden">
                            <div className="h-full rounded-full bg-teal-400 dark:bg-teal-500 transition-all" style={{ width: `${(s.sessions / maxSess) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </InsightCard>
            ) : null}

            {/* Peak Hours + Day-of-Week (adjacent) */}
            {insightsLoading ? (
              <InsightCard title="Peak Booking Hours" subtitle="When visitors are most active">
                <div className="animate-pulse h-32 bg-gray-800/40 rounded" />
              </InsightCard>
            ) : insights?.peak_hours?.some((h) => h.sessions > 0) ? (
              <InsightCard title="Peak Booking Hours" subtitle="When visitors are most active (Phoenix time)" exportData={insights.peak_hours}>
                {(() => {
                  const maxH = Math.max(...insights.peak_hours.map((h) => h.sessions));
                  const hourLabel = (h) => {
                    if (h === 0) return "12a";
                    if (h < 12) return `${h}a`;
                    if (h === 12) return "12p";
                    return `${h - 12}p`;
                  };
                  return (
                    <div>
                      <div className="flex">
                        {/* Y-axis labels */}
                        <div className="flex flex-col justify-between h-28 pr-1.5 shrink-0">
                          <span className="text-sm text-gray-400 dark:text-gray-500 leading-none">{maxH}</span>
                          <span className="text-sm text-gray-400 dark:text-gray-500 leading-none">{Math.round(maxH / 2)}</span>
                          <span className="text-sm text-gray-400 dark:text-gray-500 leading-none">0</span>
                        </div>
                        <div className="flex items-end gap-[3px] h-28 flex-1">
                          {insights.peak_hours.map(({ hour, sessions, bookings }) => {
                            const sessPct = maxH > 0 ? (sessions / maxH) * 100 : 0;
                            const bookPct = maxH > 0 ? (bookings / maxH) * 100 : 0;
                            return (
                              <div key={hour} className="flex-1 flex flex-col items-center group relative" title={`${hourLabel(hour)}: ${sessions} sessions, ${bookings} bookings`}>
                                <div className="w-full relative" style={{ height: "96px" }}>
                                  <div className="absolute bottom-0 w-full bg-[#32B1FF]/20 rounded-t transition-all" style={{ height: `${Math.max(sessPct, 2)}%` }} />
                                  <div className="absolute bottom-0 w-full bg-[#32B1FF] rounded-t transition-all" style={{ height: `${bookPct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex gap-[3px] mt-1" style={{ marginLeft: "24px" }}>
                        {insights.peak_hours.map(({ hour }) => (
                          <div key={hour} className="flex-1 text-center">
                            <span className="text-sm text-gray-400 dark:text-gray-500">{hour % 3 === 0 ? hourLabel(hour) : ""}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t dark:border-gray-700">
                        <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-[#32B1FF]/20" /><span className="text-sm text-gray-400">Sessions</span></div>
                        <div className="flex items-center gap-1.5"><div className="w-3 h-2 rounded-sm bg-[#32B1FF]" /><span className="text-sm text-gray-400">Bookings</span></div>
                      </div>
                    </div>
                  );
                })()}
              </InsightCard>
            ) : null}

            {/* Day-of-Week Search vs Stay (NEW) */}
            <InsightCard title="Search Day vs. Stay Day" subtitle="When guests shop vs. when they want to arrive">
              <DayOfWeekPanel
                headlinePct={dayOfWeek?.headline_pct}
                matrix={dayOfWeek?.matrix}
                loading={dayOfWeekLoading}
              />
            </InsightCard>

            {/* Referral Sources (existing — moved from insights) */}
            {insightsLoading ? (
              <InsightCard title="Referral Sources" subtitle="How guests say they found the property">
                <div className="animate-pulse h-32 bg-gray-800/40 rounded" />
              </InsightCard>
            ) : insights?.referral_sources?.length ? (
              <InsightCard title="Referral Sources" subtitle="How guests say they found the property (self-reported)" exportData={insights.referral_sources}>
                <div className="space-y-1.5">
                  <div className="hidden min-[500px]:flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
                    <span className="w-32 shrink-0">Source</span>
                    <div className="flex-1" />
                    <span className="w-16 text-right shrink-0">Mentions</span>
                    <span className="w-16 text-right shrink-0">Booked</span>
                  </div>
                  {(() => {
                    const maxM = insights.referral_sources[0]?.mentions || 1;
                    const barColors = [
                      "bg-[#32B1FF]", "bg-[#2DD4BF]", "bg-[#34D399]", "bg-[#FBBF24]",
                      "bg-[#FB923C]", "bg-[#F472B6]", "bg-[#A78BFA]",
                    ];
                    return insights.referral_sources.slice(0, 10).map((r, i) => {
                      const referralLabel = displayLabel(r.source);
                      return (
                      <div key={r.source} className={`flex flex-wrap items-center gap-2 gap-y-1 max-[499px]:rounded-md max-[499px]:px-2 max-[499px]:py-2 ${i % 2 === 0 ? "max-[499px]:bg-gray-50 dark:max-[499px]:bg-gray-800/40" : ""}`}>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-32 shrink-0 truncate max-[499px]:w-full max-[499px]:flex-none max-[499px]:whitespace-normal" title={referralLabel}>{referralLabel}</span>
                        <div className="flex-1 min-w-0 bg-gray-100 dark:bg-gray-800 rounded-full h-3 overflow-hidden max-[499px]:basis-full">
                          <div className={`h-full rounded-full ${barColors[i % barColors.length]}`} style={{ width: `${(r.mentions / maxM) * 100}%` }} />
                        </div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-16 text-right shrink-0 max-[499px]:w-auto">
                          <span className="hidden max-[499px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-1">Mentions</span>
                          {r.mentions}
                        </span>
                        <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 w-16 text-right shrink-0 max-[499px]:w-auto">
                          <span className="hidden max-[499px]:inline text-gray-400 dark:text-gray-500 uppercase text-xs font-semibold tracking-wider mr-1">Booked</span>
                          {r.bookings}
                        </span>
                      </div>
                      );
                    });
                  })()}
                </div>
              </InsightCard>
            ) : null}

            {/* RV Intelligence (NEW) */}
            <InsightCard title="RV Intelligence" subtitle="What guests are driving to your property" className="lg:col-span-2 xl:col-span-1" exportData={rvIntelligence?.rv_types}>
              <RvIntelligencePanel
                rvTypes={rvIntelligence?.rv_types}
                rvLengths={rvIntelligence?.rv_lengths}
                equipment={rvIntelligence?.equipment}
                slideOuts={rvIntelligence?.slide_outs}
                loading={rvIntelligenceLoading}
              />
            </InsightCard>

            {/* Pet Demand (NEW) */}
            <InsightCard title="Pet Demand" subtitle="How pet travel affects bookings" exportData={petDemand?.overview}>
              <PetDemandPanel
                overview={petDemand?.overview}
                siteTypes={petDemand?.site_types}
                loading={petDemandLoading}
              />
            </InsightCard>
          </div>
        </div>

        </>
        )}
      </div>
        </main>

      <SessionDetailPanel
        open={sessionPanelOpen}
        onClose={() => setSessionPanelOpen(false)}
        sessionId={selectedSessionId}
      />
      <CartEmailModal
        open={emailModalOpen}
        onClose={() => { setEmailModalOpen(false); setEmailModalCart(null); }}
        cart={emailModalCart}
        clientId={resolveClientId(selectedClientId)}
        onSent={(sessionId, email) => {
          // 2026-08-13 — emailStatuses is now keyed by lowercased email (not
          // session ID), matching the getCartEmailStatus lookup. If the modal
          // passes an email, use it; fall back to the cart's email.
          const key = (email || emailModalCart?.visitor_email || '').toLowerCase().trim();
          if (key) {
            setEmailStatuses(prev => ({ ...prev, [key]: { status: 'sent', sent_at: new Date().toISOString() } }));
          }
          setEmailModalOpen(false);
          setEmailModalCart(null);
        }}
      />
      <RecoveryPanel
        open={recoveryPanelOpen}
        onClose={() => setRecoveryPanelOpen(false)}
        clientId={resolveClientId(selectedClientId)}
        // 2026-07-31 — use the actual selected property name from ViewContext,
        // not the legacy `ALL_CLIENTS_MAP` fallback. Was: super-admin without
        // a specific pick fell through to "Insider Perks RV" default, which
        // then rendered as "Insider Perks RV — Email recovery performance"
        // in the panel header even when a real property was picked.
        clientName={view.clientDisplayName || view.selectedProject?.name || allowedClients.find(c => c.id === selectedClientId)?.name || "Property"}
      />
    </>
  );
};

/* ============================= HELPER COMPONENTS ============================= */
const InsightCard = ({ title, subtitle, className = "", children, exportData }) => {
  const cardRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleScreenshot = async () => {
    setMenuOpen(false);
    if (!cardRef.current) return;
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#111827", scale: 2 });
      const link = document.createElement("a");
      link.download = `${title.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      console.error("Screenshot failed:", err);
    }
  };

  const handleCsvExport = () => {
    setMenuOpen(false);
    if (!exportData?.length) return;
    const headers = Object.keys(exportData[0]);
    const rows = exportData.map((row) => headers.map((h) => {
      const v = row[h];
      return typeof v === "string" && v.includes(",") ? `"${v}"` : v ?? "";
    }).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${title.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase()}.csv`;
    link.click();
  };

  return (
    <div ref={cardRef} className={`bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5 relative ${className}`}>
      <div className="flex items-start justify-between mb-0.5">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{subtitle}</p>
        </div>
        <div className="relative shrink-0 ml-2" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Export panel"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-8 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 w-40">
              <button type="button" onClick={handleScreenshot}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <circle cx="12" cy="13" r="3" />
                </svg>
                Save as PNG
              </button>
              {exportData?.length > 0 && (
                <button type="button" onClick={handleCsvExport}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
};

const EmptyInsight = ({ message = "No data for this period." }) => (
  <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm">{message}</div>
);

/* ============================= GUEST ORIGINS MAP ============================= */
const GuestOriginsMap = ({ stateMap, maxSess, origins }) => {
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  // Keep the cursor-following tooltip fully inside the viewport — flip it to the
  // left of the cursor and nudge it up when it would otherwise clip an edge.
  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) return;
    const el = tooltipRef.current;
    const { width, height } = el.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    let left = tooltip.x + 12;
    if (left + width > vw - 8) left = tooltip.x - 12 - width;
    left = Math.max(8, Math.min(left, vw - width - 8));
    const top = Math.max(8, Math.min(tooltip.y - 10, vh - height - 8));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [tooltip]);

  return (
    <div>
      <div className="relative">
        <ComposableMap projection="geoAlbersUsa" className="w-full h-auto" style={{ maxHeight: "220px" }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const d = stateMap[name.toLowerCase()];
                const intensity = d ? Math.min(d.sessions / maxSess, 1) : 0;
                const fill = d
                  ? `rgba(129, 140, 248, ${0.15 + intensity * 0.85})`
                  : "rgba(229, 231, 235, 0.5)";
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#fff"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", fill: d ? "rgba(99, 102, 241, 0.9)" : "rgba(209, 213, 219, 0.7)" },
                      pressed: { outline: "none" },
                    }}
                    onMouseEnter={(e) => {
                      const abbr = STATE_ABBR[name] || "";
                      setTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        name,
                        abbr,
                        data: d,
                      });
                    }}
                    onMouseMove={(e) => {
                      setTooltip((prev) => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {/* Tooltip */}
        {tooltip && (
          <div
            ref={tooltipRef}
            className="fixed z-50 pointer-events-none bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm"
            style={{ left: tooltip.x + 12, top: tooltip.y - 10 }}
          >
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {tooltip.name}{tooltip.abbr ? ` (${tooltip.abbr})` : ""}
            </div>
            {tooltip.data ? (
              <div className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                {tooltip.data.sessions} sessions · {tooltip.data.bookings} booked
                {tooltip.data.revenue > 0 && ` · $${Number(tooltip.data.revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </div>
            ) : (
              <div className="text-gray-400 dark:text-gray-500 text-sm mt-0.5">No sessions</div>
            )}
          </div>
        )}
      </div>

      {/* Top 5 states + legend */}
      <div className="space-y-1 mt-2 pt-2 border-t dark:border-gray-700">
        <div className="flex items-center gap-1.5 text-sm uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
          <span className="w-3 shrink-0">#</span>
          <span className="flex-1">State</span>
          <span className="shrink-0">Sessions</span>
        </div>
        {origins.slice(0, 5).map((g, i) => (
          <div key={g.state} className="flex items-center gap-1.5">
            <span className="text-sm text-gray-400 w-3 text-right shrink-0">{i + 1}</span>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex-1 truncate">{g.state}</span>
            <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{g.sessions}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-sm text-gray-400">Fewer</span>
        <div className="flex-1 h-1.5 rounded-full" style={{ background: "linear-gradient(to right, rgba(129,140,248,0.15), rgba(129,140,248,1))" }} />
        <span className="text-sm text-gray-400">More</span>
      </div>
    </div>
  );
};

/* ============================= FUNNEL CHART ============================= */
const STAGE_LABELS = {
  search: "Search",
  results: "Results",
  details: "Details",
  cart: "Cart",
  checkout: "Checkout",
  payment: "Payment",
  confirmation: "Booked",
};

const MERGE_TO_PAYMENT = new Set(["checkout_contact", "checkout_payment"]);

const FunnelChart = ({ stages: rawStages }) => {
  // Keep checkout, merge checkout_contact/checkout_payment into "Payment"
  const merged = [];
  let paymentStage = null;
  for (const s of rawStages) {
    if (MERGE_TO_PAYMENT.has(s.stage)) {
      if (!paymentStage) {
        paymentStage = { ...s, stage: "payment" };
      } else {
        paymentStage.count = Math.min(paymentStage.count, s.count);
      }
    } else {
      if (paymentStage) { merged.push(paymentStage); paymentStage = null; }
      merged.push(s);
    }
  }
  if (paymentStage) merged.push(paymentStage);

  // Recalculate drop-off percentages
  const stages = merged.map((s, i) => {
    const prevCount = i > 0 ? merged[i - 1].count : s.count;
    const dropOffPct = prevCount > 0 ? Math.round(((prevCount - s.count) / prevCount) * 10000) / 100 : 0;
    return { ...s, drop_off_pct: dropOffPct };
  });

  const maxCount = stages[0]?.count || 1;

  // Compute worst drop-off locally (exclude first and last stage)
  let computedWorstIdx = -1;
  let computedWorstDrop = 0;
  for (let i = 1; i < stages.length - 1; i++) {
    if (stages[i].drop_off_pct > computedWorstDrop) {
      computedWorstDrop = stages[i].drop_off_pct;
      computedWorstIdx = i;
    }
  }

  return (
    <div className="space-y-2">
      {stages.map((s, i) => {
        const pct = maxCount > 0 ? (s.count / maxCount) * 100 : 0;
        const isWorst = i === computedWorstIdx;
        const isLast = i === stages.length - 1;

        // Bar color
        const barColor = isLast
          ? "bg-emerald-500 dark:bg-emerald-600"
          : isWorst
          ? "bg-red-500 dark:bg-red-600"
          : "bg-[#32B1FF] dark:bg-blue-500";

        return (
          <div key={s.stage}>
            {/* Stage row */}
            <div className="flex items-center gap-3">
              {/* Label */}
              <div className="w-20 text-right max-[409px]:text-left text-sm font-medium text-gray-600 dark:text-gray-400 shrink-0">
                {STAGE_LABELS[s.stage] || s.stage}
              </div>

              {/* Bar container */}
              <div className="flex-1 flex items-center gap-2">
                <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-7 relative overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${barColor} flex items-center justify-end pr-2`}
                    style={{ width: `${Math.max(pct, 2)}%` }}
                  >
                    {pct > 15 && (
                      <span className="text-sm font-bold text-white drop-shadow">
                        {s.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {pct <= 15 && (
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-600 dark:text-gray-300">
                      {s.count.toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Drop-off badge */}
                <div className="w-16 shrink-0 text-right">
                  {i > 0 ? (
                    <span className={`text-sm font-semibold ${
                      isWorst
                        ? "text-red-600 dark:text-red-400"
                        : s.drop_off_pct > 30
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-gray-500 dark:text-gray-400"
                    }`}>
                      -{s.drop_off_pct}%
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">--</span>
                  )}
                </div>
              </div>
            </div>

            {/* Connector arrow between bars */}
            {i < stages.length - 1 && (
              <div className="flex items-center gap-3">
                <div className="w-20" />
                <div className="flex-1 flex justify-center">
                  <svg className="w-3 h-3 text-gray-300 dark:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                </div>
                <div className="w-16" />
              </div>
            )}
          </div>
        );
      })}

      {/* Legend — stacks vertically below 410px so each item spans the full width and stays left-aligned */}
      <div className="flex flex-col min-[410px]:flex-row min-[410px]:items-center gap-2 min-[410px]:gap-4 mt-4 pt-3 border-t dark:border-gray-700">
        <div className="flex items-center gap-1.5 w-full min-[410px]:w-auto">
          <div className="w-3 h-3 rounded-sm bg-red-500 shrink-0" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Biggest drop-off</span>
        </div>
        <div className="flex items-center gap-1.5 w-full min-[410px]:w-auto">
          <div className="w-3 h-3 rounded-sm bg-emerald-500 shrink-0" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Completed booking</span>
        </div>
      </div>
    </div>
  );
};

export default BookingsPage;
